import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../services/api";
import useAuthStore from "../store/authStore";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import CouriersCompareModal from "../components/CouriersCompareModal";
import ReportDamageDialog from "../components/ReportDamageDialog";
import AgentVerifyDialog from "../components/AgentVerifyDialog";
import { Plus, Loader2, Search, Filter, Truck, Camera, Sparkles, X, IndianRupee, Box, ArrowRight, ShieldCheck, CreditCard, Download, FileText, Bike, Ruler, MapPin, Navigation, Tag, Wallet, Zap, Gift, Warehouse, CalendarDays, FileSpreadsheet, Package, Home, CheckCircle2, Clock, UserPlus, Smartphone, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import PriceCard from "../components/PriceCard";
import HubPriceCard from "../components/HubPriceCard";
import MapPicker from "../components/MapPicker";
import IndiaAddressAutocomplete, { isIndiaAddress } from "../components/IndiaAddressAutocomplete";
import BulkUploadDialog from "../components/BulkUploadDialog";
import RatingModal from "../components/RatingModal";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { RoutePreview } from "../components/orders/RoutePreview";
import { OrderList } from "../components/orders/OrderList";
import { OtpVerifyDialog } from "../components/orders/OtpVerifyDialog";
import { AssignAgentDialog } from "../components/orders/AssignAgentDialog";
import AdminCancelOrderDialog from "../components/orders/AdminCancelOrderDialog";
import OrderDetailsDialog from "../components/orders/OrderDetailsDialog";
import MultiItemEditor from "../components/orders/MultiItemEditor";
import ProofActionDialog from "../components/orders/ProofActionDialog";
import {
  serviceTypes,
  getBookingButtonLabel,
} from "../utils/orderConstants";
import useBookingDraft from "../hooks/useBookingDraft";
import BookingDraftBanner from "../components/orders/BookingDraftBanner";
import SmartAddressInput from "../components/orders/SmartAddressInput";
import useServiceabilityCheck from "../hooks/useServiceabilityCheck";
import useStateServiceability from "../hooks/useStateServiceability";
import StateServiceabilityBanner from "../components/StateServiceabilityBanner";
import PickupServiceabilityIndicator from "../components/PickupServiceabilityIndicator";
import MobilePhotoQRModal from "../components/MobilePhotoQRModal";
import { isMobile as isMobileDevice } from "../lib/device";

// AI-detected size badge color classes — extracted to avoid a nested
// ternary in JSX (#8 in the code review). Keeps the UI map data-driven.
const AI_SIZE_BADGE_CLASSES = {
  SMALL: "bg-slate-50 text-slate-700 border-slate-200",
  MEDIUM: "bg-orange-50 text-[#FF6B00] border-orange-200",
  LARGE: "bg-red-50 text-red-700 border-red-200",
};
const getAiSizeBadgeClass = (size) =>
  AI_SIZE_BADGE_CLASSES[size] || AI_SIZE_BADGE_CLASSES.LARGE;

export default function Orders() {
  const { user } = useAuthStore();
  const role = user?.role || "user";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignB2BMode, setAssignB2BMode] = useState(false);
  const [assignB2BRole, setAssignB2BRole] = useState("pickup");
  const [vehicleType, setVehicleType] = useState("van");
  const [assignCrewMode, setAssignCrewMode] = useState(false);
  const [crewNeeded, setCrewNeeded] = useState(1);
  const [selectedCrew, setSelectedCrew] = useState([]);
  const [mapPickerMode, setMapPickerMode] = useState(null); // "pickup" | "delivery" | null
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [agentsList, setAgentsList] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const groupFilter = searchParams.get("filter") || "";  // "active" | "completed" | "pending" | ""
  const [searchQuery, setSearchQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [courierCompareOpen, setCourierCompareOpen] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [damageDialogFor, setDamageDialogFor] = useState(null);
  const [verifyTarget, setVerifyTarget] = useState(null); // { order, mode: "weight"|"drop" }
  const [cancelTarget, setCancelTarget] = useState(null); // order pending admin cancellation
  const [detailsTarget, setDetailsTarget] = useState(null); // tracking_id open in OrderDetailsDialog
  // Live admin-controlled list of enabled service types. Falls back to the
  // hardcoded constants until the API resolves so the booking dialog never
  // flickers blank. Keyed by service id.
  const [enabledServiceIds, setEnabledServiceIds] = useState(null);
  const [serviceLabelOverrides, setServiceLabelOverrides] = useState({});
  const [form, setForm] = useState({
    pickup_address: "",
    delivery_address: "",
    package_description: "",
    item_size: "medium",
    package_weight: "",
    length_cm: "",
    width_cm: "",
    height_cm: "",
    notes: "",
    service_type: "delivery",
    room_type: "",
    payment_method: "cod",
    user_type: "b2c",
    order_type: "courier",
    delivery_speed: "express",
    coupon_code: "",
    bundle_id: "",
    item_count: 1,
    delivery_mode: "express",
    pickup_type: "instant",
    scheduled_date: "",
    scheduled_time: "",
    // Sprint 49 — pickup + delivery contacts (10-digit Indian mobile)
    pickup_contact_name: "",
    pickup_phone: "",
    delivery_contact_name: "",
    delivery_phone: "",
    // Sprint 52 — structured address details (optional)
    pickup_house_no: "",
    pickup_street: "",
    pickup_flat_no: "",
    delivery_house_no: "",
    delivery_street: "",
    delivery_flat_no: "",
    // Sprint 83 — optional GSTIN for B2B / GST-registered customers
    customer_gstin: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  // Sprint-83 — QR-handoff "Take photo from mobile"
  const [qrPhotoOpen, setQrPhotoOpen] = useState(false);
  const [mobilePhotoSessionId, setMobilePhotoSessionId] = useState(null);
  // Sprint-83.1 — remember WHICH source produced the current photo so
  // the Retake button reopens the same flow.  Values: 'camera' (native
  // mobile camera), 'qr' (desktop→phone QR handoff), 'upload' (file
  // picker / gallery).  null when no photo present.
  const [photoSource, setPhotoSource] = useState(null);
  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const [detecting, setDetecting] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  // Sprint-83.3 — package-details validation state.  We show inline
  // errors only after the customer has tried to submit the form once;
  // before that, empty fields render without any red noise.
  const [validationAttempted, setValidationAttempted] = useState(false);
  // Sprint 85 — GSTIN reverse lookup result. `null` = no lookup yet, object = response from /api/gstin/lookup.
  const [gstinInfo, setGstinInfo] = useState(null);
  const [gstinLoading, setGstinLoading] = useState(false);

  // Sprint-83.3 — derived per-field validation for the mandatory
  // package-details bundle: photo + weight + L + W + H. Each value
  // must parse to a positive number (>0). Errors are computed
  // unconditionally; UI only surfaces them once `validationAttempted`
  // flips to true (after the user has either submitted or touched
  // the form), matching the standard "touched + invalid" pattern.
  const _posNum = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = parseFloat(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  // Numeric-only typed input handler. Accepts an empty string and any
  // sequence of digits with at most one optional decimal point and up
  // to 2 decimal places. Rejects negatives, letters, and stray chars.
  const handleDecimalChange = (field) => (e) => {
    const v = e.target.value;
    if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
      setForm((f) => ({ ...f, [field]: v }));
    }
  };
  const pkgErrors = {
    photo: imagePreview ? null : "Please upload or take a parcel photo before continuing.",
    weight: _posNum(form.package_weight) ? null : "Please enter a valid weight in kg.",
    length: _posNum(form.length_cm) ? null : "Please enter a valid length in cm.",
    width: _posNum(form.width_cm) ? null : "Please enter a valid width in cm.",
    height: _posNum(form.height_cm) ? null : "Please enter a valid height in cm.",
  };
  const pkgValid = Object.values(pkgErrors).every((e) => !e);
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [hubPriceEstimate, setHubPriceEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  // Phase 1 P1 — Booking Progress Persistence. Auto-save form state to
  // localStorage per user_id, debounced 600 ms inside the hook. Banner
  // shows when a stored draft exists; Resume calls setForm with the
  // persisted snapshot; Discard removes it. The draft is cleared on
  // every successful order creation (COD, online, courier handoff).
  const { draft: bookingDraft, scheduleSave: scheduleDraftSave, clearDraft: clearBookingDraft, dismissDraft: dismissBookingDraft, refresh: refreshBookingDraft } = useBookingDraft(user?.id);
  const [draftBannerVisible, setDraftBannerVisible] = useState(false);
  const [ratingTarget, setRatingTarget] = useState(null);
  // Sprint 72 — Trust & Transparency proof actions
  const [proofTarget, setProofTarget] = useState(null);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpTarget, setOtpTarget] = useState(null);
  const [otpValue, setOtpValue] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [deliveryPhoto, setDeliveryPhoto] = useState("");
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [couponValid, setCouponValid] = useState(null);
  // Referral first-order discount (auto-applied server-side at checkout).
  const [referralDiscount, setReferralDiscount] = useState(null);
  useEffect(() => {
    if (role !== "user") return;
    api.get("/referral/check-discount")
      .then(({ data }) => { if (data?.has_discount) setReferralDiscount(data); })
      .catch(() => {});
  }, [role]);
  // Sprint 56 — multi-item booking state. Stays empty for single-item
  // bookings (back-compat); when populated, the per-item slab quotes
  // sum to the order total instead of using the single-shipment fields.
  const [bookingItems, setBookingItems] = useState([]);
  // Any parcel with a real weight means multi-parcel mode is active —
  // the legacy single-shipment weight + dim fields get disabled and the
  // PriceCard switches to the per-parcel breakdown.
  const hasParcels = (bookingItems || []).some(
    (it) => (it.item_name || "").trim() && Number(it.weight_kg) > 0
  );

  // Sprint-105a · multi-parcel mode requires EVERY parcel to have a name,
  // weight, all three dimensions, AND a photo. The submit button uses
  // this to gate; an inline summary message renders below the editor.
  const _parcelFieldMissing = (it) => {
    if (!(it.item_name || "").trim()) return "name";
    if (!(Number(it.weight_kg) > 0))  return "weight";
    if (!(Number(it.length_cm) > 0))  return "length";
    if (!(Number(it.width_cm)  > 0))  return "width";
    if (!(Number(it.height_cm) > 0))  return "height";
    if (!(typeof it.customer_package_photo === "string"
          && it.customer_package_photo.startsWith("data:image/"))) return "photo";
    return null;
  };
  let multiParcelMissingMsg = "";
  const multiParcelValid = (bookingItems || []).length === 0
    || (bookingItems || []).every((it, idx) => {
      const miss = _parcelFieldMissing(it);
      if (miss && !multiParcelMissingMsg) {
        multiParcelMissingMsg = `Parcel #${idx + 1} needs ${miss}`;
      }
      return miss === null;
    });

  // NOTE: `submitBlockedReason` is defined further down, *after* the
  // useStateServiceability hooks that it depends on. Putting it here
  // would trip a TDZ (`Cannot access 'stateServiceability' before
  // initialization`) on the first render.

  const lastAddressRef = useRef({ pickup: "", delivery: "" });

  // Auto-save the booking form to localStorage (debounced inside the hook).
  // Runs ONLY while the dialog is open — otherwise the resetForm() that
  // fires on close would propagate an empty form back into the hook and
  // wipe out the very draft we just persisted. Also early-return for
  // truly empty forms so a freshly-reopened dialog (form reset to
  // defaults) never auto-erases the restored draft within 600 ms of
  // open. The hook also no-ops empty inputs as a second layer.
  useEffect(() => {
    if (!user?.id) return;
    if (!dialogOpen) return;
    const hasContent = !!(
      (form.package_description && form.package_description.trim()) ||
      (form.pickup_address && form.pickup_address.trim()) ||
      (form.delivery_address && form.delivery_address.trim())
    );
    if (!hasContent) return;
    scheduleDraftSave(form);
  }, [form, user?.id, dialogOpen, scheduleDraftSave]);

  // Sprint-82 — pickup-serviceability check (debounced 500ms in the hook).
  // Drives the green/red indicator under the pickup field AND blocks submit.
  const _pickupPincodeMatch = (form.pickup_address || "").match(/\b([1-9][0-9]{5})\b/);
  const _deliveryPincodeMatch = (form.delivery_address || "").match(/\b([1-9][0-9]{5})\b/);
  const pickupServiceability = useServiceabilityCheck({
    pickup_lat: form.pickup_lat,
    pickup_lng: form.pickup_lng,
    pickup_pincode: _pickupPincodeMatch ? _pickupPincodeMatch[1] : null,
    delivery_pincode: _deliveryPincodeMatch ? _deliveryPincodeMatch[1] : null,
  });

  // Sprint-101b — state-level serviceability (replaces the old pincode-only gate
  // with a hub-active + state-aware decision). Used purely for the inline banner
  // + Notify Me + Pay & Book disable; Sprint-82 distance check still runs server-side.
  const stateServiceability = useStateServiceability({
    pincode: _pickupPincodeMatch ? _pickupPincodeMatch[1] : null,
  });

  // Sprint-105c · BUG 1 — fast state-based delivery-mode auto-detect.
  // The legacy zone-based detection (in the /pricing/estimate effect below)
  // only fires AFTER a full quote round-trip. For obviously cross-state
  // bookings (Karnataka→Kerala etc.) we can decide the mode purely from the
  // 6-digit pincodes via the existing `pincode → state` map exposed by the
  // serviceability v2 endpoint. This gives an instant correct toggle while
  // the price quote is still in-flight.
  const dropStateServiceability = useStateServiceability({
    pincode: _deliveryPincodeMatch ? _deliveryPincodeMatch[1] : null,
  });
  // Tracks whether the human has clicked a delivery-mode tile manually.
  // Once true we stop auto-detecting until the user clears the inputs.
  const deliveryModeUserOverriddenRef = useRef(false);

  // Derive the booking topology label (used as fast pre-detect + subtitle).
  const _pickupPin = _pickupPincodeMatch ? _pickupPincodeMatch[1] : null;
  const _deliveryPin = _deliveryPincodeMatch ? _deliveryPincodeMatch[1] : null;
  const _pickupState = stateServiceability.state;
  const _dropState = dropStateServiceability.state;
  let _topology = "detecting"; // detecting | same_city | cross_city | cross_state
  if (_pickupPin && _deliveryPin && _pickupState && _dropState) {
    if (_pickupState !== _dropState) _topology = "cross_state";
    else if (_pickupPin.slice(0, 3) === _deliveryPin.slice(0, 3)) _topology = "same_city";
    else _topology = "cross_city";
  }
  const _autoMode = _topology === "same_city" ? "express"
    : (_topology === "cross_city" || _topology === "cross_state") ? "hub_routed"
    : null;

  useEffect(() => {
    if (!dialogOpen) return;
    if (deliveryModeUserOverriddenRef.current) return;
    if (!_autoMode) return;
    setForm((prev) => prev.delivery_mode === _autoMode ? prev : { ...prev, delivery_mode: _autoMode });
  }, [dialogOpen, _autoMode]);

  // Reset the user-override flag when the pickup or delivery pincode changes
  // — a new lane is a new auto-detect window.
  useEffect(() => {
    deliveryModeUserOverriddenRef.current = false;
  }, [_pickupPin, _deliveryPin]);

  // Sprint-105d — Single source of truth for *why* the submit button is
  // disabled. Surfaced as the button `title`, as an inline amber caption
  // above the CTA, and (for completeness) inside handleCreate as toasts.
  // Declared HERE (after the serviceability hooks) to avoid a TDZ on the
  // first render. Returns null when the form is ready to submit.
  const submitBlockedReason = (() => {
    if (creating) return "Placing your order…";
    if (stateServiceability?.serviceable === false) {
      return stateServiceability?.message || "Pickup is not available in this state yet";
    }
    if (!pkgValid) {
      const first = Object.values(pkgErrors).find((e) => !!e);
      return first || "Complete the package details (photo, weight, dimensions)";
    }
    if (!multiParcelValid) {
      return multiParcelMissingMsg || "Each parcel needs name, dimensions, weight, and a photo";
    }
    return null;
  })();
  const submitDisabled = submitBlockedReason !== null;

  // Fetch price estimate when inputs change (debounced)
  useEffect(() => {
    if (!dialogOpen) return;
    const hasPickup = (form.pickup_address || "").trim().length >= 3;
    const hasDelivery = (form.delivery_address || "").trim().length >= 3;
    if (!hasPickup || !hasDelivery) return;

    // Use a longer debounce for non-address changes too — typing a multi-digit
    // weight (e.g. "60") would otherwise fire a fetch for "6" first and the
    // price card would briefly flash 5–10 kg slab before jumping to 50–75 kg.
    const addressChanged = form.pickup_address !== lastAddressRef.current.pickup || form.delivery_address !== lastAddressRef.current.delivery;
    const delay = addressChanged ? 1500 : 800;

    setEstimateLoading(true);
    const t = setTimeout(() => {
      lastAddressRef.current = { pickup: form.pickup_address, delivery: form.delivery_address };

      // Sprint 56 — when the customer has added valid parcels, route the
      // live quote through the multi-parcel endpoint so PriceCard reflects
      // the running grand total instead of the legacy single-shipment quote.
      //
      // Sprint-105a hotfix · ONLY send the multi-quote when EVERY parcel
      // is fully valid (name + weight + L + W + H > 0 + base64 photo). The
      // backend now hard-requires customer_package_photo per parcel and
      // returns 400 otherwise — previously this was swallowed and the
      // displayed quote froze at the single-parcel fallback.
      const allParcels = (bookingItems || []).slice(0, 10);
      const eachValid = (it) =>
        (it.item_name || "").trim().length > 0
        && Number(it.weight_kg) > 0
        && Number(it.length_cm) > 0
        && Number(it.width_cm)  > 0
        && Number(it.height_cm) > 0
        && typeof it.customer_package_photo === "string"
        && it.customer_package_photo.startsWith("data:image/");
      const allValid = allParcels.length > 0 && allParcels.every(eachValid);
      if (allParcels.length > 0 && !allValid) {
        // Mid-edit — freeze the displayed price and clear loading; do NOT
        // hit the backend with a known-invalid payload.
        setPriceEstimate((p) => (p ? { ...p, _incomplete: true } : p));
        setEstimateLoading(false);
        return;
      }
      if (allValid) {
        const parcels = allParcels.map((it) => ({
          item_name: it.item_name.trim(),
          weight_kg: parseFloat(it.weight_kg) || 0,
          length_cm: parseFloat(it.length_cm) || 0,
          width_cm:  parseFloat(it.width_cm)  || 0,
          height_cm: parseFloat(it.height_cm) || 0,
          quantity:  Math.max(1, parseInt(it.quantity || 1, 10)),
          customer_package_photo: it.customer_package_photo,
        }));
        api.post("/pricing/estimate-multi", {
          pickup: form.pickup_address,
          delivery: form.delivery_address,
          items: parcels,
          service_type: form.service_type,
          item_size: form.item_size || "small",
          // Phase-105f — pass the coupon through to the multi-quote so
          // the displayed grand total reflects the discount instead of
          // showing the pre-discount value.
          coupon_code: couponValid ? form.coupon_code : undefined,
          payment_method: form.payment_method,
        })
          .then(({ data }) => {
            setPriceEstimate({ ...data, _incomplete: false });
            const zone = data?.zone || "";
            if (zone === "A") setForm((prev) => ({ ...prev, delivery_mode: "express" }));
            else if (zone) setForm((prev) => ({ ...prev, delivery_mode: "hub_routed" }));
            // If the server-side coupon validation flipped against us
            // (expired / over-cap / already used after a re-quote),
            // drop the discount and warn the user inline.
            if (couponValid && data?.coupon_error) {
              setCouponValid(false);
              toast.warning(data.coupon_error);
            }
          })
          .catch((err) => {
            const code = err?.response?.status;
            if (code === 401) return;
            // 400 = backend validation. 500 / network = surface to user.
            if (code && code !== 400) {
              toast.error("Couldn't refresh price — try again");
            }
            console.warn("[multi-quote]", code, err?.response?.data || err.message);
          })
          .finally(() => setEstimateLoading(false));
        return;
      }

      const params = {
        item_size: form.item_size,
        weight: form.package_weight || undefined,
        pickup: form.pickup_address || undefined,
        delivery: form.delivery_address || undefined,
        service_type: form.service_type,
        room_type: form.room_type || undefined,
        volume_cm3: aiResult?.volume_cm3 || undefined,
        user_type: form.user_type,
        order_type: form.order_type,
        item_count: form.item_count || 1,
        delivery_speed: form.delivery_speed,
        coupon_code: couponValid ? form.coupon_code : undefined,
        bundle_id: form.bundle_id || undefined,
        payment_method: form.payment_method,
      };
      // Pass dimensions if available — AI-detected dims take precedence,
      // otherwise fall back to manual entry from the booking form.
      if (aiResult?.estimated_length) {
        params.length = parseFloat(aiResult.estimated_length) || undefined;
        params.width = parseFloat(aiResult.estimated_width) || undefined;
        params.height = parseFloat(aiResult.estimated_height) || undefined;
      } else if (form.length_cm && form.width_cm && form.height_cm) {
        params.length = parseFloat(form.length_cm) || undefined;
        params.width = parseFloat(form.width_cm) || undefined;
        params.height = parseFloat(form.height_cm) || undefined;
      }
      api.get("/pricing/estimate", { params })
        .then(({ data }) => {
          setPriceEstimate(data);
          // Auto-detect delivery mode based on zone
          const zone = data?.zone || "";
          if (zone === "A") {
            setForm((prev) => ({ ...prev, delivery_mode: "express" }));
          } else if (zone) {
            setForm((prev) => ({ ...prev, delivery_mode: "hub_routed" }));
          }
        })
        .catch((err) => { if (err.response?.status !== 401) console.error(err.message); })
        .finally(() => setEstimateLoading(false));
    }, delay);
    return () => clearTimeout(t);
    // Intentionally omit form.coupon_code — we only want the coupon to affect the quote AFTER it's been validated (tracked via couponValid).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.item_size, form.package_weight, form.length_cm, form.width_cm, form.height_cm, form.pickup_address, form.delivery_address, form.service_type, form.room_type, form.user_type, form.order_type, form.item_count, form.delivery_speed, form.bundle_id, form.payment_method, couponValid, aiResult, dialogOpen, bookingItems]);

  // Sprint 85 — GSTIN reverse lookup. Debounced 400ms; cancels stale
  // requests so a fast-typing user doesn't see flicker.
  useEffect(() => {
    const g = (form.customer_gstin || "").trim();
    const valid = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(g);
    if (!valid) {
      setGstinInfo(null);
      setGstinLoading(false);
      return;
    }
    let cancelled = false;
    setGstinLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/gstin/lookup/${g}`);
        if (!cancelled) setGstinInfo(data);
      } catch {
        if (!cancelled) setGstinInfo({ error: "lookup_failed" });
      } finally {
        if (!cancelled) setGstinLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.customer_gstin]);


  // Fetch hub pricing estimate for hub_routed mode
  useEffect(() => {
    if (!dialogOpen || form.delivery_mode !== "hub_routed") {
      setHubPriceEstimate(null);
      return;
    }
    // Sprint 56 — when the customer is using multi-parcel mode the live
    // grand total comes from /pricing/estimate-multi, so suppress the
    // single-shipment hub-pricing call (otherwise PriceCard would show
    // the per-1-parcel hub quote instead of the parcels sum).
    const hasParcels = (bookingItems || []).some((it) => (it.item_name || "").trim() && Number(it.weight_kg) > 0);
    if (hasParcels) {
      setHubPriceEstimate(null);
      return;
    }
    const hasPickup = (form.pickup_address || "").trim().length >= 3;
    const hasDelivery = (form.delivery_address || "").trim().length >= 3;
    if (!hasPickup || !hasDelivery) return;

    const hubAddressChanged = form.pickup_address !== lastAddressRef.current.pickup || form.delivery_address !== lastAddressRef.current.delivery;
    const hubDelay = hubAddressChanged ? 1500 : 800;

    setEstimateLoading(true);
    const t = setTimeout(() => {
      // Pass dimensions to hub-pricing so volumetric weight (= L×W×H/5000)
      // is honored. AI-detected dims take precedence over manual ones.
      const params = {
        pickup_address: form.pickup_address,
        delivery_address: form.delivery_address,
        weight_kg: form.package_weight || 1,
        storage_hours: 0,
        item_size: form.item_size,
        delivery_speed: form.delivery_speed,
      };
      if (aiResult?.estimated_length) {
        params.length_cm = parseFloat(aiResult.estimated_length) || 0;
        params.width_cm = parseFloat(aiResult.estimated_width) || 0;
        params.height_cm = parseFloat(aiResult.estimated_height) || 0;
      } else if (form.length_cm && form.width_cm && form.height_cm) {
        params.length_cm = parseFloat(form.length_cm) || 0;
        params.width_cm = parseFloat(form.width_cm) || 0;
        params.height_cm = parseFloat(form.height_cm) || 0;
      }
      if (aiResult?.volume_cm3) {
        params.volume_cm3 = aiResult.volume_cm3;
      }
      api.post("/hub-pricing/auto-calculate", null, { params })
        .then(({ data }) => setHubPriceEstimate(data))
        .catch(() => setHubPriceEstimate(null))
        .finally(() => setEstimateLoading(false));
    }, hubDelay);
    return () => clearTimeout(t);
  }, [form.pickup_address, form.delivery_address, form.package_weight, form.delivery_mode, form.item_size, form.delivery_speed, form.length_cm, form.width_cm, form.height_cm, aiResult, dialogOpen, bookingItems]);

  const fetchOrders = useCallback(async () => {
    try {
      const endpoint = role === "admin" ? "/orders/all" : "/orders/my-orders";
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      const { data } = await api.get(endpoint, { params });
      setOrders(data);
    } catch (err) {
      console.error("Fetch orders error:", err);
    } finally {
      setLoading(false);
    }
  }, [role, filterStatus]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    if (dialogOpen && (role === "user" || role === "admin")) {
      api.get("/wallet/balance").then(({ data }) => setWalletBalance(data)).catch((err) => { if (err.response?.status !== 401) console.error(err.message); });
    }
  }, [dialogOpen, role]);

  useEffect(() => {
    if (role === "admin") {
      api.get("/agents/list").then(({ data }) => setAgentsList(data)).catch((err) => { if (err.response?.status !== 401) console.error(err.message); });
    }
  }, [role]);

  useEffect(() => {
    // Only fetch when the booking dialog is opened so initial page loads stay light.
    if (!dialogOpen) return;
    api.get("/service-types")
      .then(({ data }) => {
        const list = data?.service_types || [];
        const ids = new Set(list.map((s) => s.id));
        const labels = {};
        for (const s of list) {
          if (s.label) labels[s.id] = s.label;
        }
        setEnabledServiceIds(ids);
        setServiceLabelOverrides(labels);
        // If the currently selected service got disabled while the dialog
        // was previously open, snap back to the first enabled one.
        setForm((prev) => {
          if (ids.has(prev.service_type)) return prev;
          const fallback = list[0]?.id || "delivery";
          return { ...prev, service_type: fallback, room_type: "" };
        });
      })
      .catch(() => {/* keep defaults on failure — do not block booking */});
  }, [dialogOpen]);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Only JPEG, PNG, or WEBP images are supported");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }
    // Sprint-83.1 — determine source from which hidden input fired so
    // Retake can reopen the same flow.  e.target is the actual <input>.
    const src = e?.target?.dataset?.source === "camera" ? "camera" : "upload";
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setAiResult(null);
    setPhotoSource(src);
    // Allow re-selecting the same file next time.
    try { e.target.value = ""; } catch { /* noop */ }
  };

  // Open the right "Take Photo" flow based on the current device class.
  const openTakePhoto = () => {
    if (isMobileDevice()) {
      cameraInputRef.current?.click();
    } else {
      setQrPhotoOpen(true);
    }
  };

  // Retake reopens whichever source produced the current photo.
  const retakePhoto = () => {
    const src = photoSource;
    setImageFile(null);
    setImagePreview(null);
    setAiResult(null);
    if (mobilePhotoSessionId) {
      api.delete(`/customer/photo-session/${mobilePhotoSessionId}`).catch(() => {});
      setMobilePhotoSessionId(null);
    }
    if (src === "camera") {
      setTimeout(() => cameraInputRef.current?.click(), 60);
    } else if (src === "qr") {
      setTimeout(() => setQrPhotoOpen(true), 60);
    } else {
      setTimeout(() => uploadInputRef.current?.click(), 60);
    }
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setAiResult(null);
    setPhotoSource(null);
    if (mobilePhotoSessionId) {
      // Best-effort cleanup of the Mongo blob (no await — fire & forget).
      api.delete(`/customer/photo-session/${mobilePhotoSessionId}`).catch(() => {});
      setMobilePhotoSessionId(null);
    }
  };

  // Sprint-83 — photo received from the phone via QR session.
  // We download the image into a real File object so the rest of the
  // booking flow (AI detect-size multipart upload, /scan-item base64
  // enrichment, /orders/create with `photo_url`) keeps working
  // identically to a desktop file pick.
  const handleMobilePhotoReceived = async (imageUrl, sessionId) => {
    try {
      const res = await fetch(imageUrl, { credentials: "include" });
      const blob = await res.blob();
      const mime = blob.type || "image/jpeg";
      const ext = mime.split("/")[1] || "jpg";
      const file = new File([blob], `mobile-${sessionId.slice(0, 8)}.${ext}`, { type: mime });
      setImageFile(file);
      setImagePreview(URL.createObjectURL(blob));
      setAiResult(null);
      setMobilePhotoSessionId(sessionId);
      setPhotoSource("qr");
      // Sprint-83.2 — auto-fire AI dimension detection on mobile-handoff
      // arrival ONLY (not gallery uploads). handleDetectSize already
      // wraps the API call in try/catch so an unset EMERGENT_LLM_KEY,
      // network blip, or low-confidence response will silently fall
      // through without surfacing an error toast. The manual "Detect
      // Item & Size" button remains available as a retry path.
      handleDetectSize(file, true).catch(() => { /* silent — manual retry available */ });
    } catch (e) {
      toast.error("Could not load photo from mobile. Please try again.");
    }
  };

  const handleDetectSize = async (overrideFile = null, silent = false) => {
    const fileToUse = overrideFile || imageFile;
    // Sprint-99 — Guard: never fire the API with a missing payload. This was
    // the actual user-reported symptom — toast `image: Field required` when
    // the multipart serialiser produced an empty body.
    console.debug("[detect-size]", {
      fileToUse_kind: fileToUse instanceof File ? "File" : typeof fileToUse,
      file_name: fileToUse?.name,
      file_size: fileToUse?.size,
      imageFile_set: !!imageFile,
    });
    if (!fileToUse) {
      if (!silent) toast.error("Photo not loaded yet — please retake or wait a second.");
      return;
    }
    setDetecting(true);
    setAiResult(null);
    try {
      // Sprint-96 — Route on type. If somehow a string (image URL or data URL)
      // landed in state instead of a File, hit the JSON sibling endpoint so we
      // don't trip FastAPI's strict `UploadFile = File(...)` validator.
      let data;
      if (typeof fileToUse === "string") {
        const payload = fileToUse.startsWith("data:")
          ? { image_base64: fileToUse }
          : { image_url: fileToUse };
        ({ data } = await api.post("/ai/detect-size-by-url", payload));
      } else {
        const formData = new FormData();
        formData.append("image", fileToUse);
        // Sprint-99 — `api` defaults to Content-Type: application/json.
        // Pass undefined so axios+browser auto-pick `multipart/form-data;
        // boundary=...`. Without this, FormData was being JSON-serialised
        // to "[object FormData]" and the backend saw NO image field at
        // all → "image: Field required".
        ({ data } = await api.post("/ai/detect-size", formData, {
          headers: { "Content-Type": undefined },
        }));
      }

      // Also get dimensions + volume from scanner
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(fileToUse);
      });
      let scanData = null;
      try {
        const scanRes = await api.post("/scan-item", { image_base64: base64 });
        scanData = scanRes.data;
      } catch (err) {
        // /scan-item is a best-effort enrichment; keep scanData null and continue.
        console.warn("scan-item enrichment skipped:", err.response?.status || err.message);
      }

      const mergedResult = {
        ...data,
        volume_cm3: scanData?.volume_cm3 || null,
        estimated_length: scanData?.estimated_length || null,
        estimated_width: scanData?.estimated_width || null,
        estimated_height: scanData?.estimated_height || null,
      };
      setAiResult(mergedResult);

      const sizeMap = { SMALL: "small", MEDIUM: "medium", LARGE: "large" };
      const mappedSize = sizeMap[data.recommended_size] || "medium";
      const fmtNum = (n) => {
        const x = Number(n);
        if (!Number.isFinite(x) || x <= 0) return "";
        return x % 1 === 0 ? String(x) : x.toFixed(1);
      };
      setForm((prev) => ({
        ...prev,
        item_size: mappedSize,
        package_description: prev.package_description || data.item_detected,
        package_weight: prev.package_weight || (data.estimated_weight ? String(data.estimated_weight) : prev.package_weight),
        // Auto-fill the visible dimension inputs from the AI scanner so the
        // customer can review/tweak them before the price card recalculates.
        length_cm: fmtNum(scanData?.estimated_length) || prev.length_cm,
        width_cm: fmtNum(scanData?.estimated_width) || prev.width_cm,
        height_cm: fmtNum(scanData?.estimated_height) || prev.height_cm,
      }));
      if (!silent) toast.success(`Detected: ${data.item_detected} — ${data.recommended_size}${scanData?.volume_cm3 ? ` (${scanData.volume_cm3.toLocaleString()} cm³)` : ""}`);
    } catch (err) {
      if (!silent) toast.error(err.userMessage || "Detection failed. Select size manually.");
    } finally {
      setDetecting(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!form.coupon_code.trim()) return;
    try {
      await api.post("/coupons/apply", { code: form.coupon_code });
      setCouponValid(true);
      toast.success("Coupon applied!");
    } catch (err) {
      setCouponValid(false);
      toast.error(err.response?.data?.detail || "Invalid coupon");
    }
  };

  const resetForm = () => {
    setForm({ pickup_address: "", delivery_address: "", package_description: "", item_size: "medium", package_weight: "", length_cm: "", width_cm: "", height_cm: "", notes: "", service_type: "delivery", room_type: "", payment_method: "cod", user_type: "b2c", order_type: "courier", delivery_speed: "express", coupon_code: "", bundle_id: "", item_count: 1, delivery_mode: "express", pickup_type: "instant", scheduled_date: "", scheduled_time: "", pickup_contact_name: "", pickup_phone: "", delivery_contact_name: "", delivery_phone: "", pickup_house_no: "", pickup_street: "", pickup_flat_no: "", delivery_house_no: "", delivery_street: "", delivery_flat_no: "", customer_gstin: "" });
    clearImage();
    setPriceEstimate(null);
    setHubPriceEstimate(null);
    setCouponValid(null);
    setSelectedCourier(null);
    setBookingItems([]);
  };

  // Phase 1 P1 — separate "order successfully placed" path. Clears the
  // in-memory form AND the persisted localStorage draft so the next
  // booking starts fresh. NOTE: plain dialog close uses resetForm() only
  // so the saved draft survives and the customer can resume on re-open.
  const resetAfterOrderPlaced = () => {
    resetForm();
    clearBookingDraft();
  };

  const handleCreate = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    // Sprint-105d — defensive entry log + never-silent guarantee. Every
    // early-return below now produces a user-visible toast so a "click did
    // nothing" report becomes impossible to reproduce.
    console.warn("[handleCreate] CLICK", {
      payment_method: form.payment_method,
      pkgValid,
      multiParcelValid,
      creating,
      submitBlockedReason,
      stateServiceability,
      dropStateServiceability,
      bookingItems_len: (bookingItems || []).length,
    });
    if (creating) {
      toast.info("Already placing your order…");
      return;
    }
    // Sprint-83.3 — mandatory package details. Surface errors and
    // refuse to submit if any of photo/weight/L/W/H are missing or
    // non-positive.
    if (!pkgValid) {
      setValidationAttempted(true);
      toast.error("Please complete the highlighted package details (photo + weight + dimensions) before booking.");
      return;
    }
    if (form.pickup_type === "scheduled" && (!form.scheduled_date || !form.scheduled_time)) {
      toast.error("Please select a pickup date and time");
      return;
    }
    if (!multiParcelValid) {
      toast.error(multiParcelMissingMsg || "Each parcel needs a name, dimensions, weight, and a photo");
      return;
    }
    if (stateServiceability?.serviceable === false) {
      toast.error(stateServiceability?.message || "Pickup not available in your state yet");
      return;
    }

    // Sprint-101c — Sprint-82 distance gate removed from client.
    // The state-level v2 gate handles unserviceable pickups (button is
    // already disabled), and the backend re-checks state on order create.

    // India-only guard. Trust autocomplete picks (they're already filtered by
    // countrycodes=in) and skip the network round-trip when the user picked
    // from the dropdown. Only validate manually-typed addresses.
    if (!form.pickup_lat || !form.delivery_lat) {
      const [pickupOk, deliveryOk] = await Promise.all([
        form.pickup_lat ? Promise.resolve(true) : isIndiaAddress(form.pickup_address),
        form.delivery_lat ? Promise.resolve(true) : isIndiaAddress(form.delivery_address),
      ]);
      if (!pickupOk || !deliveryOk) {
        toast.error("We currently support deliveries within India only.");
        return;
      }
    }

    // Sprint 77 — Trust & Transparency: require BOTH a package photo
    // AND manual L/W/H so we always have proof + dimensions on the order.
    // Skipped for room/office shifting (uses room_type estimate) and
    // multi-parcel mode (dimensions set per parcel below).
    const isRoomShift = form.service_type === "room_shifting" || form.service_type === "office_shifting";
    if (!isRoomShift && !hasParcels) {
      const hasManual = !!(form.length_cm && form.width_cm && form.height_cm
        && parseFloat(form.length_cm) > 0 && parseFloat(form.width_cm) > 0 && parseFloat(form.height_cm) > 0);
      const hasPhoto = !!imagePreview;
      if (!hasPhoto || !hasManual) {
        toast.error("Please add a package photo AND enter L × W × H — both are required.");
        return;
      }
    }

    // Sprint 77 — courier must be explicitly picked by the customer.
    // We no longer auto-route to "platform picks best" silently.
    if (!isRoomShift && role !== "agent" && !selectedCourier) {
      toast.error("Please pick a courier before placing the order.");
      setCourierCompareOpen(true);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="courier-compare-section"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }

    setCreating(true);
    try {
      // Sprint 72 fix — read package photo as a real base64 data URL.
      // Previously we sent `imagePreview` directly, which is a transient
      // blob: URL from URL.createObjectURL() and is unreachable from the
      // backend or any other browser, so the customer photo never showed
      // up on the public tracking page or hub manager dashboard.
      let customerPhotoBase64 = null;
      if (imageFile) {
        customerPhotoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(imageFile);
        });
      }

      // Build a unified `dimensions_cm` string from AI OR manual inputs so
      // the assigned agent/admin always sees the parcel's dimensions in the
      // order list, regardless of how they were captured.
      const manualL = parseFloat(form.length_cm);
      const manualW = parseFloat(form.width_cm);
      const manualH = parseFloat(form.height_cm);
      const hasManualDims = Number.isFinite(manualL) && manualL > 0
        && Number.isFinite(manualW) && manualW > 0
        && Number.isFinite(manualH) && manualH > 0;
      const aiDimsString = aiResult?.dimensions_cm
        || (aiResult?.estimated_length ? `${aiResult.estimated_length} x ${aiResult.estimated_width} x ${aiResult.estimated_height}` : null);
      const manualDimsString = hasManualDims ? `${manualL} x ${manualW} x ${manualH}` : null;
      const finalDimsString = aiDimsString || manualDimsString;
      const finalVolume = aiResult?.volume_cm3 || (hasManualDims ? Math.round(manualL * manualW * manualH) : null);

      const orderPayload = {
        pickup_address: form.pickup_address,
        delivery_address: form.delivery_address,
        package_description: form.package_description,
        item_size: form.item_size,
        package_weight: form.package_weight ? parseFloat(form.package_weight) : null,
        notes: aiResult?.packing_tips ? `${form.notes ? form.notes + " | " : ""}AI Tip: ${aiResult.packing_tips}` : (form.notes || null),
        service_type: form.service_type,
        room_type: form.room_type || null,
        volume_cm3: finalVolume,
        // Persist parcel dimensions so backend can compute volumetric weight
        // (= L × W × H / 5000) and bill on max(actual, volumetric). AI-detected
        // dims take precedence, otherwise manual entries from the booking form.
        length_cm: aiResult?.estimated_length ? parseFloat(aiResult.estimated_length) : (hasManualDims ? manualL : null),
        width_cm: aiResult?.estimated_width ? parseFloat(aiResult.estimated_width) : (hasManualDims ? manualW : null),
        height_cm: aiResult?.estimated_height ? parseFloat(aiResult.estimated_height) : (hasManualDims ? manualH : null),
        delivery_mode: form.delivery_mode,
        selected_courier: selectedCourier ? (() => {
          const _livePct = Number(hubPriceEstimate?.cod_fee_pct ?? priceEstimate?.cod_fee_pct ?? 5);
          const codFee = form.payment_method === "cod" ? Math.round(selectedCourier.courier_price * _livePct / 100) : 0;
          return {
            ...selectedCourier,
            cod_fee: codFee,
            courier_price: selectedCourier.courier_price + codFee,
            base_price: selectedCourier.courier_price,
          };
        })() : null,
        ai_dimensions: (aiResult || finalDimsString) ? {
          item_detected: aiResult?.item_detected || form.package_description || null,
          recommended_size: aiResult?.recommended_size || (form.item_size ? form.item_size.toUpperCase() : null),
          dimensions_cm: finalDimsString,
          volume_cm3: finalVolume,
          packing_tips: aiResult?.packing_tips || null,
          source: aiResult ? "ai" : "manual",
        } : (priceEstimate?.items_estimate ? {
          room_type: form.room_type,
          items_estimate: priceEstimate.items_estimate,
          crew_size: priceEstimate.crew_size,
          vehicle_type: priceEstimate.vehicle_type,
        } : null),
        user_type: form.user_type,
        order_type: form.order_type,
        item_count: form.item_count || 1,
        delivery_speed: form.delivery_speed,
        coupon_code: couponValid ? form.coupon_code : null,
        bundle_id: form.bundle_id || null,
        pickup_type: form.pickup_type || "instant",
        scheduled_date: form.scheduled_date || null,
        scheduled_time: form.scheduled_time || null,
        // Sprint 49 — pickup + delivery contacts (validated client-side via validatePhone)
        pickup_contact_name: form.pickup_contact_name?.trim() || "",
        pickup_phone: form.pickup_phone?.replace(/\D/g, "") || "",
        delivery_contact_name: form.delivery_contact_name?.trim() || "",
        delivery_phone: form.delivery_phone?.replace(/\D/g, "") || "",
        // Sprint 52 — structured address details (free-form, optional)
        pickup_house_no: form.pickup_house_no?.trim() || "",
        pickup_street: form.pickup_street?.trim() || "",
        pickup_flat_no: form.pickup_flat_no?.trim() || "",
        delivery_house_no: form.delivery_house_no?.trim() || "",
        delivery_street: form.delivery_street?.trim() || "",
        delivery_flat_no: form.delivery_flat_no?.trim() || "",
        // Sprint 56 — multi-item lines (max 10). null = single-item flow.
        items: (() => {
          const valid = (bookingItems || [])
            .filter((it) => (it.item_name || "").trim() && Number(it.weight_kg) > 0)
            .slice(0, 10);
          if (!valid.length) return null;
          return valid.map((it) => ({
            item_name: it.item_name.trim(),
            weight_kg: parseFloat(it.weight_kg) || 0,
            length_cm: it.length_cm ? parseFloat(it.length_cm) : null,
            width_cm: it.width_cm ? parseFloat(it.width_cm) : null,
            height_cm: it.height_cm ? parseFloat(it.height_cm) : null,
            quantity: Math.max(1, parseInt(it.quantity || 1, 10)),
          }));
        })(),
        // Sprint 72 — Trust & Transparency: persist customer's package photo so
        // it appears on the public tracking page from minute zero.
        customer_package_photo: customerPhotoBase64,
        // Sprint 83 — Optional GSTIN for B2B / GST-registered customers.
        // Sent only when populated; backend validates format & silently
        // drops bad values rather than rejecting the booking.
        customer_gstin: form.customer_gstin?.trim().toUpperCase() || null,
      };

      // Admin uses the same payment flow as customers (Feb 2026):
      // COD → /payments/create-cod, Online → Razorpay popup, Wallet → /payments/create-wallet.
      // The order is attributed to the admin's account (out-of-pocket booking).
      if (form.payment_method === "wallet") {
        const { data } = await api.post("/payments/create-wallet", orderPayload);
        toast.success(`Order placed! Tracking ID: ${data.tracking_id}. Wallet balance: INR ${data.wallet_balance}`);
        if (data.volume_reward_coupon) {
          toast.success(`Coupon earned: ${data.volume_reward_coupon.code} — 20% off next courier!`, { duration: 8000 });
        }
      } else if (form.payment_method === "cod") {
        const { data } = await api.post("/payments/create-cod", orderPayload);
        toast.success(`Order placed! Tracking ID: ${data.tracking_id}. Pay on delivery.`);
        if (data.volume_reward_coupon) {
          toast.success(`Coupon earned: ${data.volume_reward_coupon.code} — 20% off next courier!`, { duration: 8000 });
        }
      } else {
        // ─── Razorpay online payment — UPI Intent + Redirect Flow ───
        // 1. Initiate online payment (creates Razorpay order + stashes intent — no DB order)
        const { data: rzOrder } = await api.post("/payments/initiate-online", orderPayload);

        // Sprint-97 — Mock-mode short-circuit. In preview, Razorpay is mocked;
        // skip the real SDK and confirm directly with a synthetic signature so
        // the booking flow lights up end-to-end without a payment provider.
        if (rzOrder.mock || rzOrder.key_id === "rzp_test_mock") {
          try {
            const { data: confirmed } = await api.post("/payments/confirm-online", {
              razorpay_order_id: rzOrder.razorpay_order_id,
              razorpay_payment_id: `pay_mock_${rzOrder.razorpay_order_id.slice(-8)}`,
              razorpay_signature: "MOCK_SIGNATURE",
            });
            toast.success(`Mock payment confirmed · ${confirmed.tracking_id}`);
            setDialogOpen(false);
            resetForm();
            window.location.href = `/payment-success?razorpay_order_id=${rzOrder.razorpay_order_id}&tracking_id=${confirmed.tracking_id}`;
          } catch (e) {
            toast.error(e?.userMessage || "Mock payment confirm failed");
          }
          return;
        }

        // 2. Lazy-load Razorpay checkout — with error + timeout handling so a
        //    blocked/failed script (adblock, network, CSP) surfaces a clear
        //    message instead of hanging forever with no popup.
        if (!window.Razorpay) {
          document.getElementById("razorpay-script")?.remove();
          const script = document.createElement("script");
          script.id = "razorpay-script";
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          document.body.appendChild(script);
          try {
            await new Promise((resolve, reject) => {
              const t = setTimeout(() => reject(new Error("timeout")), 15000);
              script.onload = () => { clearTimeout(t); resolve(); };
              script.onerror = () => { clearTimeout(t); reject(new Error("load-failed")); };
            });
          } catch (scriptErr) {
            script.remove();
            toast.error("Couldn't load the payment window. Please check your internet connection or disable ad-blockers, then try again.");
            setCreating(false);
            return;
          }
        }
        if (!window.Razorpay) {
          toast.error("Payment window unavailable. Please refresh the page and try again.");
          setCreating(false);
          return;
        }

        // Persist the rz_order_id locally so PaymentSuccess can look it up
        // even if Razorpay's redirect strips a query param on some carriers.
        try {
          sessionStorage.setItem("pickngo_pending_rz_order", rzOrder.razorpay_order_id);
        } catch (storageErr) {
          // sessionStorage may be disabled (private mode, full quota, etc.).
          // Non-fatal: PaymentSuccess will fall back to the URL params Razorpay
          // redirects with. Log so the issue is visible in the console.
          console.warn("sessionStorage unavailable, skipping rz_order persist:", storageErr);
        }

        // ─── CRITICAL: close the booking Dialog BEFORE opening Razorpay ───
        // Radix UI Dialog (shadcn) applies `pointer-events: none` to <body>
        // while it's open (focus-trapping). Razorpay's checkout iframe is
        // injected into <body>, so its tap events get swallowed — that's
        // why on mobile the "More options" / UPI tiles look correct but
        // taps do nothing. Closing the Dialog here releases the body lock.
        // We wait two animation frames so Radix has time to remove its
        // pointer-events style before Razorpay's iframe mounts.
        setDialogOpen(false);
        resetForm();
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

        // 3. Open Razorpay in MODAL/HANDLER mode (NOT redirect mode).
        //    Why modal: `redirect: true` is incompatible with UPI Intent
        //    deep-links on Android — the parent navigation races the
        //    `intent://` URL and most browsers cancel the deep-link mid
        //    flight, so GPay / PhonePe / Paytm never open.
        //    With handler mode Razorpay's own iframe handles the deep-link
        //    correctly and returns the verified payment params via
        //    `handler(response)` once the user comes back from the UPI app.
        //    Order creation is still gated by backend `/confirm-online`
        //    (signature verify) — frontend never marks paid by itself.
        //
        //    IMPORTANT (UPI Intent on mobile):
        //    DO NOT override `config.display.blocks.upi` with a custom
        //    `instruments` array — doing so collapses Razorpay's mobile
        //    renderer to a generic "Enter UPI ID" Collect screen and the
        //    GPay / PhonePe / Paytm Intent app picker never shows (taps
        //    bounce back to the Razorpay sheet).
        //    DO NOT pass an explicit `method` filter — it can also
        //    suppress the auto-detected Intent flow.
        //    Razorpay Standard Checkout auto-detects mobile devices and
        //    renders the native Intent picker out of the box. Keep this
        //    config minimal.
        const rzOptions = {
          key: rzOrder.key_id,
          amount: rzOrder.amount,
          currency: rzOrder.currency,
          name: "PickNGo",
          description: `Pickup booking — ₹${rzOrder.amount_inr}`,
          order_id: rzOrder.razorpay_order_id,
          theme: { color: "#FF6B00" },
          prefill: {
            name: user?.name || "",
            email: user?.email || "",
            // Razorpay accepts both 10-digit and +91-prefixed contacts.
            // Strip non-digits and keep last 10 — matches their validator.
            contact: (user?.phone || "").toString().replace(/\D/g, "").slice(-10),
          },
          notes: { rz_order_id: rzOrder.razorpay_order_id },
          // Handler runs after the user successfully completes payment.
          // Razorpay passes the 3 signed params; we POST them to backend
          // `/confirm-online` which (a) HMAC-verifies the signature and
          // (b) atomically creates the order from the stashed intent.
          handler: async (response) => {
            try {
              const { data } = await api.post("/payments/confirm-online", {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              sessionStorage.removeItem("pickngo_pending_rz_order");
              toast.success(`Payment verified! Order ${data?.tracking_id || "placed"}`);
              setDialogOpen(false);
              resetAfterOrderPlaced();
              fetchOrders();
            } catch (err) {
              // Signature verification or DB insert failed. The webhook
              // (payment.captured) will retry order creation idempotently
              // within a few seconds — show user a soft error and route to
              // /payment-failed so they aren't stuck.
              const desc = encodeURIComponent(err.response?.data?.detail || "Verification failed");
              toast.error("Payment verification failed");
              window.location.href = `/payment-failed?error_code=VERIFY_FAILED&error_description=${desc}&order_id=${response.razorpay_order_id || ""}`;
            }
          },
          modal: {
            // User closed the Razorpay sheet without completing payment.
            // No order was created (backend only creates on /confirm-online),
            // so we just surface a clean "cancelled" UX.
            ondismiss: () => {
              sessionStorage.removeItem("pickngo_pending_rz_order");
              toast("Payment cancelled. Order not booked.");
              setCreating(false);
            },
          },
        };
        const rzp = new window.Razorpay(rzOptions);
        // Hard payment failure (UPI declined, insufficient balance, bank
        // outage, etc.). Razorpay emits this in modal/handler mode with the
        // full error object. Order is NOT created — backend only creates on
        // verified /confirm-online. We route the user to /payment-failed.
        rzp.on("payment.failed", (resp) => {
          const code = encodeURIComponent(resp?.error?.code || "PAYMENT_FAILED");
          const desc = encodeURIComponent(resp?.error?.description || "Payment failed. Order not booked.");
          const oid = encodeURIComponent(resp?.error?.metadata?.order_id || rzOrder.razorpay_order_id || "");
          sessionStorage.removeItem("pickngo_pending_rz_order");
          window.location.href = `/payment-failed?error_code=${code}&error_description=${desc}&order_id=${oid}`;
        });
        rzp.open();
        setCreating(false);
        return;
      }
      setDialogOpen(false);
      resetAfterOrderPlaced();
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create order");
    } finally {
      setCreating(false);
    }
  };

  const handleAcceptPickup = async (trackingId) => {
    setUpdatingId(trackingId);
    try {
      await api.post(`/orders/accept/${trackingId}`);
      toast.success(`Accepted order ${trackingId}!`);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept order");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStatusAdvance = async (trackingId, currentStatus, orderType) => {
    // For in_transit (express) or out_for_delivery (hub), open OTP dialog
    if (currentStatus === "in_transit" || currentStatus === "out_for_delivery") {
      setOtpTarget(trackingId);
      setOtpValue("");
      setOtpDialogOpen(true);
      return;
    }

    // For at_dest_hub: agent self-accepts, admin opens assign dialog to pick delivery agent
    if (currentStatus === "at_dest_hub") {
      if (role === "agent") {
        setUpdatingId(trackingId);
        try {
          await api.post(`/orders/accept-delivery/${trackingId}`);
          toast.success("Delivery accepted! OTP sent to customer.");
          fetchOrders();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Failed to accept delivery");
          fetchOrders();
        } finally {
          setUpdatingId(null);
        }
      } else {
        // Admin: open assign dialog to pick a delivery agent
        setAssignTarget(trackingId);
        setAssignDialogOpen(true);
      }
      return;
    }

    setUpdatingId(trackingId);
    try {
      // Use B2B advance endpoint for B2B orders
      if (orderType === "b2b_express") {
        await api.post("/b2b/advance-status", { tracking_id: trackingId });
      } else if (orderType === "b2b_return") {
        await api.post("/b2b/advance-return-status", { tracking_id: trackingId });
      } else {
        await api.put(`/orders/update-status/${trackingId}`);
      }
      toast.success("Status updated!");
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
      fetchOrders();
    } finally {
      setUpdatingId(null);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpTarget || !otpValue.trim()) return;
    if (!recipientName.trim()) { toast.error("Recipient name is required"); return; }
    if (!deliveryPhoto) { toast.error("A delivery proof photo is required"); return; }
    setVerifyingOtp(true);
    try {
      await api.post(`/orders/${otpTarget}/verify-otp`, {
        otp: otpValue.trim(),
        recipient_name: recipientName.trim(),
        photo_base64: deliveryPhoto,
      });
      toast.success("OTP verified! Delivery confirmed.");
      setOtpDialogOpen(false);
      setOtpTarget(null);
      setOtpValue(""); setRecipientName(""); setDeliveryPhoto("");
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleConfirmCod = async (trackingId) => {
    setUpdatingId(trackingId);
    try {
      await api.post(`/orders/${trackingId}/confirm-cod-payment`);
      toast.success("COD payment confirmed!");
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to confirm payment");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAssign = async () => {
    if (!assignTarget) return;
    try {
      if (assignB2BMode && selectedAgent) {
        await api.post("/b2b/assign-agent", {
          tracking_id: assignTarget,
          agent_id: selectedAgent,
          vehicle_type: vehicleType,
          role: assignB2BRole,
        });
        toast.success(`B2B ${assignB2BRole} agent assigned with ${vehicleType}!`);
      } else if (assignCrewMode && selectedCrew.length > 0) {
        await api.put(`/orders/assign-crew/${assignTarget}`, { agent_ids: selectedCrew });
        toast.success(`${selectedCrew.length} crew members assigned!`);
      } else if (selectedAgent) {
        // Phase 105-Agent-C — use the canonical PATCH path. The legacy
        // PUT /orders/assign/{tid} flipped status straight to
        // "assigned" which then blocked the agent's /accept flow
        // (state machine rejected it). PATCH keeps status pending so
        // the agent acceptance lifecycle works end-to-end AND fires
        // the agent_assigned notification.
        await api.patch(`/orders/${assignTarget}/assign`, {
          role: "pickup_agent",
          agent_user_id: selectedAgent,
        });
        toast.success("Agent assigned!");
      } else return;
      setAssignDialogOpen(false);
      setAssignTarget(null);
      setSelectedAgent("");
      setSelectedCrew([]);
      setAssignCrewMode(false);
      setAssignB2BMode(false);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign");
    }
  };

  const toggleCrewMember = (agentId) => {
    setSelectedCrew((prev) =>
      prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId]
    );
  };

  const FILTER_GROUPS = {
    active: new Set(["assigned", "pickup_assigned", "accepted", "picked_up", "in_transit", "at_origin_hub", "linehaul", "in_linehaul", "at_dest_hub", "out_for_delivery"]),
    completed: new Set(["delivered"]),
    pending: new Set(["pending", "payment_pending", "awaiting_assignment", "draft"]),
    // Phase ASSIGN-FIX-r8 — admin nudge: orders still waiting for a pickup
    // agent. Combined with the !pickup_agent_name predicate below in
    // filteredOrders so the chip only counts ACTUALLY unassigned rows.
    needs_assignment: new Set(["pending"]),
  };
  const FILTER_LABELS = {
    active: "Active", completed: "Completed", pending: "Pending",
    needs_assignment: "Pending Assignment",
  };

  const filteredOrders = orders.filter((order) => {
    if (groupFilter && FILTER_GROUPS[groupFilter] && !FILTER_GROUPS[groupFilter].has(order.status)) {
      return false;
    }
    // Extra predicate for the needs_assignment nudge — pickup unassigned.
    if (groupFilter === "needs_assignment"
        && (order.pickup_agent_name || order.assigned_pickup_agent_name
            || order.pickup_agent_id || order.assigned_pickup_agent_id)) {
      return false;
    }
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      order.tracking_id?.toLowerCase().includes(q) ||
      order.package_description?.toLowerCase().includes(q) ||
      order.pickup_address?.toLowerCase().includes(q) ||
      order.delivery_address?.toLowerCase().includes(q)
    );
  });

  const clearGroupFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  };

  const getPageTitle = () => {
    if (role === "admin") return "All Orders";
    if (role === "agent") return "My Assignments";
    return "My Orders";
  };
  const getPageDesc = () => {
    if (role === "admin") return "Manage all platform orders and assign agents";
    if (role === "agent") return "Orders assigned to you — update their status";
    return "Track and manage your deliveries";
  };
  const pageTitle = getPageTitle();
  const pageDesc = getPageDesc();

  const handleExportOrders = (type) => {
    const token = localStorage.getItem("token");
    const base = api.defaults.baseURL || "";
    const url = `${base}/admin/export/orders-${type}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `pickngo_orders.${type}`;
        a.click();
        URL.revokeObjectURL(u);
      });
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fadeInUp">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900" data-testid="orders-heading">{pageTitle}</h1>
          <p className="text-sm text-slate-500 mt-1">{pageDesc}</p>
        </div>
        <div className="flex items-center gap-2">
          {role === "admin" && (
            <>
              <Button variant="outline" onClick={() => handleExportOrders("csv")} className="rounded-xl border-slate-200 text-sm" data-testid="export-orders-csv">
                <Download className="h-4 w-4 mr-1.5" /> CSV
              </Button>
              <Button variant="outline" onClick={() => handleExportOrders("pdf")} className="rounded-xl border-slate-200 text-sm" data-testid="export-orders-pdf">
                <FileText className="h-4 w-4 mr-1.5" /> PDF
              </Button>
            </>
          )}
        {(role === "user" || role === "admin") && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            if (!open && (mapPickerMode || courierCompareOpen)) return;
            setDialogOpen(open);
            if (open) {
              // Phase 1 P1 — re-read localStorage on open so a draft
              // saved earlier in the same session (or from another tab)
              // is reflected in the banner. The hook's React state was
              // a mount-time snapshot otherwise.
              const fresh = refreshBookingDraft();
              setDraftBannerVisible(Boolean(fresh && fresh.form));
            } else {
              resetForm();
              setDraftBannerVisible(false);
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl px-6 font-medium shadow-sm shadow-[#FF6B00]/20 hover:shadow-md hover:shadow-[#FF6B00]/25 btn-press" data-testid="create-order-btn">
                <Plus className="h-5 w-5 mr-2" />
                Book Pickup
              </Button>
            </DialogTrigger>
            {(role === "user" || role === "admin") && (
              <Button variant="outline" onClick={() => setBulkUploadOpen(true)} className="rounded-xl px-4 font-medium border-slate-200 hover:border-[#FF6B00] hover:text-[#FF6B00]" data-testid="bulk-upload-btn">
                <FileSpreadsheet className="h-4 w-4 mr-1.5" />
                Bulk CSV
              </Button>
            )}
            <DialogContent className="sm:max-w-xl rounded-2xl p-0 flex flex-col gap-0 max-h-[90vh] h-[100dvh] sm:h-auto" data-testid="create-order-dialog" aria-describedby="create-order-desc" onPointerDownOutside={(e) => { if (mapPickerMode) e.preventDefault(); }} onInteractOutside={(e) => { if (mapPickerMode) e.preventDefault(); }}>
              <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-2 space-y-1">
                <DialogTitle className="font-['Outfit'] text-xl font-semibold text-slate-900">Book a Pickup</DialogTitle>
                <p id="create-order-desc" className="text-sm text-slate-500">Fill in the details to schedule a new pickup</p>
              </DialogHeader>
              <form onSubmit={handleCreate} className="flex flex-col flex-1 min-h-0">
                {/* Sprint-105e — scrollable body. Limits overflow to this
                    container so the sticky footer (caption + Place Order CTA)
                    is always visible without trailing dead whitespace. */}
                <div className="flex-1 overflow-y-auto px-6 pt-1 pb-4 space-y-4" data-testid="create-order-body">
                <BookingDraftBanner
                  draft={draftBannerVisible ? bookingDraft : null}
                  onResume={() => {
                    if (bookingDraft?.form) {
                      // Merge persisted fields onto the current form
                      // shape so any newly-added (post-draft) fields keep
                      // their default values.
                      setForm((prev) => ({ ...prev, ...bookingDraft.form }));
                    }
                    setDraftBannerVisible(false);
                    toast.success("Draft restored");
                  }}
                  onDiscard={() => {
                    dismissBookingDraft();
                    setDraftBannerVisible(false);
                  }}
                />
                {/* Service Type Selector */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                    <Truck className="h-3.5 w-3.5 text-[#FF6B00]" />
                    Service Type
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="service-type-selector">
                    {serviceTypes.map((svc) => {
                      // Keep all 5 cards visible — if the admin has disabled this
                      // service from /dashboard/service-types, we render it as a
                      // greyed-out, un-clickable card with a red "Not Available"
                      // tag so the customer understands we offer it but it's
                      // paused right now (vs hiding it entirely, which looks
                      // like the service never existed).
                      const disabled = enabledServiceIds && !enabledServiceIds.has(svc.value);
                      const Icon = svc.icon;
                      const selected = form.service_type === svc.value && !disabled;
                      const displayLabel = serviceLabelOverrides[svc.value] || svc.label;
                      return (
                        <button
                          key={svc.value}
                          type="button"
                          onClick={() => {
                            if (disabled) return;
                            setForm({ ...form, service_type: svc.value, room_type: "" });
                          }}
                          disabled={disabled}
                          aria-disabled={disabled}
                          title={disabled ? `${displayLabel} is not available right now` : undefined}
                          className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                            disabled
                              ? "border-slate-200 bg-slate-50/70 opacity-60 cursor-not-allowed grayscale"
                              : selected
                                ? "border-[#FF6B00] bg-orange-50/50 ring-2 ring-[#FF6B00]/20"
                                : `${svc.color} hover:border-slate-300`
                          }`}
                          data-testid={`service-type-${svc.value}`}
                        >
                          {disabled && (
                            <span
                              className="absolute -top-1.5 -right-1.5 z-10 inline-flex items-center gap-1 bg-red-500 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full shadow-sm"
                              data-testid={`service-disabled-badge-${svc.value}`}
                            >
                              <span className="w-1 h-1 bg-white rounded-full" />
                              Not Available
                            </span>
                          )}
                          <Icon className={`h-5 w-5 ${disabled ? "text-slate-400" : (selected ? "text-[#FF6B00]" : "text-slate-500")}`} />
                          <span className={`text-xs font-semibold leading-tight ${disabled ? "text-slate-400 line-through" : (selected ? "text-[#FF6B00]" : "text-slate-700")}`}>{displayLabel}</span>
                          {disabled && (
                            <span className="text-[9px] text-red-500 font-semibold mt-0.5">Temporarily paused</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {form.service_type !== "delivery" && (
                    <p className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2" data-testid="service-type-note">
                      {form.service_type === "room_shifting" && "A truck and packing crew will be assigned for your room move."}
                      {form.service_type === "luggage_shifting" && "A van will pick up and securely deliver your luggage on time."}
                      {form.service_type === "office_shifting" && "Professional team with truck for IT equipment and office furniture."}
                      {form.service_type === "small_furniture" && "A van with bubble wrap protection for your furniture item."}
                    </p>
                  )}
                </div>

                {/* Room Type Selector for Room/Office Shifting */}
                {(form.service_type === "room_shifting" || form.service_type === "office_shifting") ? (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-[#FF6B00]" />
                      {form.service_type === "room_shifting" ? "Room Type" : "Office Size"}
                      <span className="text-[10px] bg-orange-100 text-[#FF6B00] px-1.5 py-0.5 rounded-full font-bold ml-1">AI PRICING</span>
                    </Label>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="room-type-selector">
                      {[
                        { value: "1bhk", label: "1 BHK", officeLabel: "Small" },
                        { value: "2bhk", label: "2 BHK", officeLabel: "Medium" },
                        { value: "3bhk", label: "3 BHK", officeLabel: "Large" },
                        { value: "villa", label: "Villa", officeLabel: "Full Floor" },
                        { value: "apartment", label: "Apartment", officeLabel: "Co-work" },
                      ].map((rt) => {
                        const sel = form.room_type === rt.value;
                        return (
                          <button
                            key={rt.value}
                            type="button"
                            onClick={() => setForm({ ...form, room_type: rt.value })}
                            className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                              sel ? "border-[#FF6B00] bg-orange-50 ring-2 ring-[#FF6B00]/20" : "border-slate-200 bg-white hover:border-slate-300"
                            }`}
                            data-testid={`room-type-${rt.value}`}
                          >
                            <Home className={`h-5 w-5 ${sel ? "text-[#FF6B00]" : "text-slate-400"}`} />
                            <span className={`text-xs font-semibold ${sel ? "text-[#FF6B00]" : "text-slate-700"}`}>
                              {form.service_type === "office_shifting" ? rt.officeLabel : rt.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {form.room_type && priceEstimate?.items_estimate && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 animate-fadeInUp" data-testid="room-type-ai-estimate">
                        <div className="flex items-center gap-2 mb-1.5">
                          <Sparkles className="h-4 w-4 text-blue-600" />
                          <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">AI Estimate</span>
                        </div>
                        <p className="text-sm text-blue-800 font-medium">Estimated items: {priceEstimate.items_estimate}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-blue-600">
                          <span className="flex items-center gap-1"><Truck className="h-3 w-3" /> {priceEstimate.vehicle_type?.replace("_", " ")}</span>
                          {priceEstimate.crew_size && <span className="flex items-center gap-1"><UserPlus className="h-3 w-3" /> {priceEstimate.crew_size} crew members</span>}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                <>
                {/* AI Detection — Sprint 72: now framed as MANDATORY proof photo */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                    <Camera className="h-3.5 w-3.5 text-[#FF6B00]" />
                    Package photo
                    <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold ml-1">REQUIRED</span>
                    <span className="text-[10px] bg-orange-100 text-[#FF6B00] px-1.5 py-0.5 rounded-full font-bold">AI AUTO-SIZE</span>
                  </Label>
                  <p className="text-[11px] text-slate-500 -mt-1">
                    Visible to you on the tracking page as proof of what you sent. AI will also auto-detect dimensions to compute fair pricing — or you can enter L×W×H manually below.
                  </p>
                  <div className={`border-2 border-dashed rounded-xl transition-all duration-200 ${imagePreview ? "border-[#FF6B00]/30 bg-orange-50/30" : "border-slate-200 bg-slate-50/50 hover:border-slate-300"}`} data-testid="ai-upload-zone">
                    {!imagePreview ? (
                      <div className="flex gap-3 py-5 px-3" data-testid="photo-source-buttons">
                        {/* Sprint-83.1 — Take Photo: device-aware (camera on phone, QR on desktop) */}
                        <button
                          type="button"
                          onClick={openTakePhoto}
                          className="flex-1 flex flex-col items-center gap-2 cursor-pointer p-3 rounded-xl border-2 border-transparent hover:border-[#FF6B00]/20 hover:bg-orange-50/30 transition-all"
                          data-testid="ai-take-photo-btn"
                        >
                          <div className="w-12 h-12 bg-[#FF6B00] rounded-xl flex items-center justify-center">
                            <Camera className="h-6 w-6 text-white" />
                          </div>
                          <div className="text-center">
                            <span className="text-sm font-medium text-slate-700 block">Take Photo</span>
                            <span className="text-[10px] text-slate-400">{isMobileDevice() ? "Open camera" : "Scan QR with phone"}</span>
                          </div>
                        </button>
                        {/* Upload Photo — file picker, identical on every device */}
                        <button
                          type="button"
                          onClick={() => uploadInputRef.current?.click()}
                          className="flex-1 flex flex-col items-center gap-2 cursor-pointer p-3 rounded-xl border-2 border-transparent hover:border-[#FF6B00]/20 hover:bg-orange-50/30 transition-all"
                          data-testid="ai-upload-photo-btn"
                        >
                          <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                            <FileText className="h-6 w-6 text-[#FF6B00]" />
                          </div>
                          <div className="text-center">
                            <span className="text-sm font-medium text-slate-700 block">Upload Photo</span>
                            <span className="text-[10px] text-slate-400">From gallery / files</span>
                          </div>
                        </button>
                        {/* Hidden inputs — one with capture (camera), one without (gallery) */}
                        <input
                          ref={cameraInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          capture="environment"
                          className="hidden"
                          data-source="camera"
                          onChange={handleImageSelect}
                          data-testid="ai-camera-input"
                        />
                        <input
                          ref={uploadInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          data-source="upload"
                          onChange={handleImageSelect}
                          data-testid="ai-image-input"
                        />
                        {validationAttempted && pkgErrors.photo && (
                          <p className="absolute -bottom-1 left-3 right-3 text-[11px] text-red-600 flex items-center gap-1" data-testid="photo-error" style={{position:'static',marginTop:'4px'}}>
                            <span className="inline-block w-1 h-1 bg-red-500 rounded-full" />{pkgErrors.photo}
                          </p>
                        )}
                      </div>
                    ) : (<div className="p-3 space-y-3">
                        <div className="relative">
                          <img src={imagePreview} alt="Item preview" className="w-full h-36 object-contain rounded-lg bg-white border border-slate-100" data-testid="ai-image-preview" />
                          {photoSource && (
                            <span className="absolute top-2 left-2 bg-slate-900/80 text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md" data-testid="photo-source-badge">
                              {photoSource === "camera" ? "Camera" : photoSource === "qr" ? "From mobile" : "Uploaded"}
                            </span>
                          )}
                        </div>
                        {/* Sprint-83.1 — Retake (re-opens same source) + Remove */}
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={retakePhoto}
                            className="h-9 text-sm font-medium border-slate-200"
                            data-testid="photo-retake-btn"
                          >
                            <Camera className="h-3.5 w-3.5 mr-1.5" /> Retake
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={clearImage}
                            className="h-9 text-sm font-medium border-red-100 text-red-600 hover:bg-red-50 hover:border-red-200"
                            data-testid="photo-remove-btn"
                          >
                            <X className="h-3.5 w-3.5 mr-1.5" /> Remove
                          </Button>
                        </div>
                        {!aiResult && (
                          <Button type="button" onClick={() => handleDetectSize()} disabled={detecting} className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-lg h-10 text-sm font-medium btn-press" data-testid="ai-detect-btn">
                            {detecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                            {detecting ? "Analyzing with AI..." : "Detect Item & Size"}
                          </Button>
                        )}
                        {aiResult && (
                          <div className="bg-white border border-green-200 rounded-xl p-3.5 shadow-sm animate-scaleIn" data-testid="ai-result">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center">
                                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                                </div>
                                <span className="text-xs font-bold text-green-700 uppercase tracking-wider">AI Detected</span>
                              </div>
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border ${getAiSizeBadgeClass(aiResult.recommended_size)}`} data-testid="ai-size-badge">
                                <Box className="h-3 w-3" />
                                {aiResult.recommended_size}
                              </span>
                            </div>
                            <p className="text-sm font-semibold text-slate-900 capitalize" data-testid="ai-item-name">{aiResult.item_detected}</p>
                            {aiResult.dimensions_cm && <p className="text-xs text-slate-500 mt-1">Box: {aiResult.dimensions_cm}</p>}
                            {aiResult.packing_tips && <p className="text-xs text-slate-500 mt-0.5 italic">Tip: {aiResult.packing_tips}</p>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                </>
                )}

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Item Description</Label>
                  <Input placeholder="What are you sending?" value={form.package_description} onChange={(e) => setForm({ ...form, package_description: e.target.value })} className="rounded-xl border-slate-200 h-11 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20" required data-testid="order-description-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SmartAddressInput
                    id="pickup"
                    label="Pickup Address"
                    placeholder="e.g. MG Road, Bangalore 560001"
                    value={form.pickup_address}
                    onChange={(v) => setForm((f) => ({ ...f, pickup_address: v }))}
                    onSelectLocation={(loc) => setForm((f) => ({
                      ...f,
                      pickup_address: loc.address,
                      pickup_lat: loc.lat,
                      pickup_lng: loc.lng,
                      pickup_osm_id: loc.osm_id,
                      pickup_place_id: loc.place_id,
                    }))}
                    onCurrentLocation={(loc) => setForm((f) => ({
                      ...f,
                      pickup_address: loc.address,
                      pickup_lat: loc.lat,
                      pickup_lng: loc.lng,
                      pickup_osm_id: loc.osm_id,
                      pickup_place_id: loc.place_id,
                    }))}
                    onMapPick={() => { setDialogOpen(false); setMapPickerMode("pickup"); }}
                    structured={{
                      house_no: form.pickup_house_no,
                      street: form.pickup_street,
                      flat_no: form.pickup_flat_no,
                    }}
                    onFieldChange={(sub, val) => setForm((f) => ({
                      ...f,
                      [`pickup_${sub}`]: val,
                    }))}
                  />
                  <SmartAddressInput
                    id="delivery"
                    label="Drop Address"
                    placeholder="e.g. Jubilee Hills, Hyderabad 500033"
                    value={form.delivery_address}
                    onChange={(v) => setForm((f) => ({ ...f, delivery_address: v }))}
                    onSelectLocation={(loc) => setForm((f) => ({
                      ...f,
                      delivery_address: loc.address,
                      delivery_lat: loc.lat,
                      delivery_lng: loc.lng,
                      delivery_osm_id: loc.osm_id,
                      delivery_place_id: loc.place_id,
                    }))}
                    onCurrentLocation={(loc) => setForm((f) => ({
                      ...f,
                      delivery_address: loc.address,
                      delivery_lat: loc.lat,
                      delivery_lng: loc.lng,
                      delivery_osm_id: loc.osm_id,
                      delivery_place_id: loc.place_id,
                    }))}
                    onMapPick={() => { setDialogOpen(false); setMapPickerMode("delivery"); }}
                    structured={{
                      house_no: form.delivery_house_no,
                      street: form.delivery_street,
                      flat_no: form.delivery_flat_no,
                    }}
                    onFieldChange={(sub, val) => setForm((f) => ({
                      ...f,
                      [`delivery_${sub}`]: val,
                    }))}
                  />
                </div>

                {/* Sprint-101b — state-level serviceability banner (Notify Me
                    + Pay & Book block). Sits above the Sprint-82 lat/lng
                    indicator. */}
                <StateServiceabilityBanner
                  sv={stateServiceability}
                  pincode={_pickupPincodeMatch ? _pickupPincodeMatch[1] : ""}
                  prefillEmail={user?.email || ""}
                  source="booking_modal"
                  className="mb-2"
                />

                {/* Sprint-101c — Sprint-82 PickupServiceabilityIndicator
                    removed. v2 state-level banner above is now the single
                    source of truth; rendering both produced contradictory
                    cards (e.g. "Pickup available in Kerala" + "259 km from
                    nearest hub"). Pricing engine still uses lat/lng
                    distance via useServiceabilityCheck internally. */}

                {/* Sprint 49 — pickup + delivery contacts. Required for ops:
                    failed-pickup callback, failed-delivery escalation, OTP verification.
                    Hidden from PriceCard (pre-booking quote stays clean).
                    Real-time 10-digit Indian-mobile validation. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="contact-fields-row">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Pickup Contact Name <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder="Who will hand over the package?"
                      value={form.pickup_contact_name}
                      onChange={(e) => setForm((f) => ({ ...f, pickup_contact_name: e.target.value }))}
                      className="rounded-xl border-slate-200 h-11 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
                      required
                      data-testid="order-pickup-contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Pickup Phone <span className="text-red-500">*</span></Label>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      placeholder="10-digit mobile (e.g. 9876543210)"
                      value={form.pickup_phone}
                      onChange={(e) => setForm((f) => ({ ...f, pickup_phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                      className={`rounded-xl border-slate-200 h-11 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 ${
                        form.pickup_phone && !/^[6-9]\d{9}$/.test(form.pickup_phone) ? "border-red-300 focus:border-red-500 focus:ring-red-500/20" : ""
                      }`}
                      required
                      maxLength={10}
                      data-testid="order-pickup-contact-phone"
                    />
                    {form.pickup_phone && !/^[6-9]\d{9}$/.test(form.pickup_phone) && (
                      <p className="text-[11px] text-red-500">10 digits, starts with 6/7/8/9</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Delivery Contact Name <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder="Who will receive the package?"
                      value={form.delivery_contact_name}
                      onChange={(e) => setForm((f) => ({ ...f, delivery_contact_name: e.target.value }))}
                      className="rounded-xl border-slate-200 h-11 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
                      required
                      data-testid="order-delivery-contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Delivery Phone <span className="text-red-500">*</span></Label>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      placeholder="10-digit mobile (e.g. 9876543210)"
                      value={form.delivery_phone}
                      onChange={(e) => setForm((f) => ({ ...f, delivery_phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                      className={`rounded-xl border-slate-200 h-11 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 ${
                        form.delivery_phone && !/^[6-9]\d{9}$/.test(form.delivery_phone) ? "border-red-300 focus:border-red-500 focus:ring-red-500/20" : ""
                      }`}
                      required
                      maxLength={10}
                      data-testid="order-delivery-contact-phone"
                    />
                    {form.delivery_phone && !/^[6-9]\d{9}$/.test(form.delivery_phone) && (
                      <p className="text-[11px] text-red-500">10 digits, starts with 6/7/8/9</p>
                    )}
                  </div>
                </div>
                {/* Sprint 83 — Optional GSTIN. Sprint 85.1 — Only shown when B2B selected to reduce B2C noise. */}
                {form.user_type === "b2b" && (
                <div className="space-y-2 pt-1" data-testid="gstin-section">
                  <Label className="text-sm font-medium text-slate-700">
                    Customer GSTIN <span className="text-[11px] text-slate-400 font-normal">(optional — needed for ITC claim & GST invoice)</span>
                  </Label>
                  <Input
                    placeholder="e.g. 29ABCDE1234F1Z5"
                    value={form.customer_gstin}
                    onChange={(e) => setForm((f) => ({ ...f, customer_gstin: e.target.value.toUpperCase().slice(0, 15) }))}
                    className={`rounded-xl border-slate-200 h-11 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 font-mono uppercase ${
                      form.customer_gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.customer_gstin)
                        ? "border-amber-300 focus:border-amber-500 focus:ring-amber-500/20"
                        : ""
                    }`}
                    maxLength={15}
                    data-testid="order-customer-gstin"
                  />
                  {!form.customer_gstin && (
                    <p className="text-[11px] text-slate-500">Skip if you don&apos;t have a GSTIN — we&apos;ll issue a regular B2C invoice.</p>
                  )}
                  {form.customer_gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.customer_gstin) && (
                    <p className="text-[11px] text-amber-600">15-character GSTIN format. We&apos;ll print it on your invoice once valid.</p>
                  )}
                  {form.customer_gstin && /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.customer_gstin) && (
                    <p className="text-[11px] text-emerald-600">✓ Valid GSTIN — will appear on invoice.</p>
                  )}
                  {/* Sprint 85 — Reverse-lookup card. Shows decoded state instantly + (when API key set) live business name. */}
                  {gstinLoading && (
                    <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-500" data-testid="gstin-lookup-loading">
                      Looking up GSTIN…
                    </div>
                  )}
                  {gstinInfo && !gstinInfo.error && (
                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] space-y-0.5" data-testid="gstin-lookup-result">
                      {gstinInfo.business_name && (
                        <div className="text-emerald-800 font-semibold">{gstinInfo.business_name}</div>
                      )}
                      {gstinInfo.state_name && (
                        <div className="text-emerald-700">
                          State: <span className="font-medium">{gstinInfo.state_name}</span>
                          {gstinInfo.state_code && <span className="text-emerald-600"> ({gstinInfo.state_code})</span>}
                        </div>
                      )}
                      {gstinInfo.registration_status && (
                        <div className="text-emerald-700">Status: <span className="font-medium">{gstinInfo.registration_status}</span></div>
                      )}
                      {gstinInfo.is_mock && (
                        <div className="text-amber-600 text-[10px] pt-1">⚠ Mock data — live lookup not configured</div>
                      )}
                      {gstinInfo.source === "local_only" && (
                        <div className="text-slate-500 text-[10px] pt-1">Local decode only (no API key)</div>
                      )}
                    </div>
                  )}
                </div>
                )}
                {form.service_type !== "room_shifting" && form.service_type !== "office_shifting" && (
                <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">
                      Item Size {aiResult && <span className="text-[#FF6B00] text-[10px] font-bold">(AI)</span>}
                    </Label>
                    <Select value={form.item_size} onValueChange={(v) => setForm({ ...form, item_size: v })}>
                      <SelectTrigger className={`rounded-xl h-11 ${aiResult ? "border-[#FF6B00] ring-1 ring-[#FF6B00]/20" : "border-slate-200"}`} data-testid="order-size-select">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        <SelectItem value="small">Small (Envelope/Docs)</SelectItem>
                        <SelectItem value="medium">Medium (Box/Package)</SelectItem>
                        <SelectItem value="large">Large (Furniture/Heavy)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">
                      Weight (kg) <span className="text-red-500">*</span>
                      {hasParcels && <span className="ml-1.5 text-[10px] text-slate-400 font-normal">(disabled — using parcels below)</span>}
                    </Label>
                    <div className="relative">
                      <Input
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        placeholder={hasParcels ? "Enter weight per parcel below" : "e.g. 2.5"}
                        value={hasParcels ? "" : form.package_weight}
                        onChange={handleDecimalChange("package_weight")}
                        className={`no-spinner rounded-xl border-slate-200 h-11 pr-12 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 ${hasParcels ? "bg-slate-50 cursor-not-allowed" : ""} ${validationAttempted && pkgErrors.weight ? "border-red-300 focus:border-red-400 focus:ring-red-100" : ""}`}
                        data-testid="order-weight-input"
                        disabled={hasParcels}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 pointer-events-none">kg</span>
                    </div>
                    {!hasParcels && validationAttempted && pkgErrors.weight && (
                      <p className="text-[11px] text-red-600 flex items-center gap-1" data-testid="weight-error"><span className="inline-block w-1 h-1 bg-red-500 rounded-full" />{pkgErrors.weight}</p>
                    )}
                  </div>
                </div>
                {/* Manual dimensions — mandatory. When AI scan runs it pre-fills
                    these same fields; users can still edit the result. */}
                <div className="space-y-2" data-testid="manual-dimensions-section">
                  <Label className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
                    <Box className="h-3.5 w-3.5 text-slate-400" />
                    Package dimensions <span className="text-red-500">*</span>
                    {hasParcels
                      ? <span className="text-[10px] text-slate-400 font-normal">(disabled — set per parcel below)</span>
                      : aiResult
                        ? <span className="text-[10px] text-emerald-600 font-medium">✨ Auto-filled from AI — tap to edit</span>
                        : <span className="text-[10px] text-slate-400 font-normal">(or let AI detect from photo)</span>
                    }
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "length_cm", label: "L", errKey: "length", testid: "order-length-input" },
                      { key: "width_cm",  label: "W", errKey: "width",  testid: "order-width-input"  },
                      { key: "height_cm", label: "H", errKey: "height", testid: "order-height-input" },
                    ].map((f) => (
                      <div key={f.key} className="relative">
                        <Input
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          placeholder={`${f.label} (cm)`}
                          value={hasParcels ? "" : (form[f.key] ?? "")}
                          onChange={handleDecimalChange(f.key)}
                          className={`no-spinner rounded-xl border-slate-200 h-10 pr-9 text-sm focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 ${hasParcels ? "bg-slate-50 cursor-not-allowed" : ""} ${validationAttempted && pkgErrors[f.errKey] ? "border-red-300 focus:border-red-400 focus:ring-red-100" : ""}`}
                          data-testid={f.testid}
                          disabled={hasParcels}
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-slate-400 pointer-events-none">cm</span>
                      </div>
                    ))}
                  </div>
                  {!hasParcels && validationAttempted && (pkgErrors.length || pkgErrors.width || pkgErrors.height) && (
                    <div className="space-y-0.5 mt-1">
                      {pkgErrors.length && <p className="text-[11px] text-red-600 flex items-center gap-1" data-testid="length-error"><span className="inline-block w-1 h-1 bg-red-500 rounded-full" />{pkgErrors.length}</p>}
                      {pkgErrors.width && <p className="text-[11px] text-red-600 flex items-center gap-1" data-testid="width-error"><span className="inline-block w-1 h-1 bg-red-500 rounded-full" />{pkgErrors.width}</p>}
                      {pkgErrors.height && <p className="text-[11px] text-red-600 flex items-center gap-1" data-testid="height-error"><span className="inline-block w-1 h-1 bg-red-500 rounded-full" />{pkgErrors.height}</p>}
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400">
                    {hasParcels
                      ? "Multi-parcel mode: enter weight + dimensions for each parcel below. Each is priced independently."
                      : "Tip: large light parcels are charged on volumetric weight = (L × W × H) / 5000."
                    }
                  </p>
                </div>
                </>
                )}
                {/* Sprint 56 — Multi-item add-ons. Hidden for shifting/bundle services. */}
                <MultiItemEditor
                  items={bookingItems}
                  onChange={setBookingItems}
                  disabled={["room_shifting", "office_shifting", "luggage_shifting", "small_furniture"].includes(form.service_type)}
                />
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Notes</Label>
                  <Input placeholder="Special instructions (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl border-slate-200 h-11 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20" data-testid="order-notes-input" />
                </div>

                {/* B2B / B2C Toggle */}
                <div className="space-y-2" data-testid="user-type-section">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                    <Wallet className="h-3.5 w-3.5 text-[#FF6B00]" />
                    Customer Type
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: "b2c", label: "B2C (Personal)", icon: Package, desc: "Residential delivery" },
                      { value: "b2b", label: "B2B (Business)", icon: Wallet, desc: walletBalance?.tier ? `${walletBalance.tier_label} — INR ${walletBalance.balance?.toLocaleString()}` : "GST invoice · ITC claim" },
                    ].map((t) => {
                      const Icon = t.icon;
                      const sel = form.user_type === t.value;
                      return (
                        <button key={t.value} type="button" onClick={() => setForm((prev) => ({
                          ...prev,
                          user_type: t.value,
                          payment_method: t.value === "b2b" && walletBalance?.balance > 0 ? "wallet" : "cod",
                          // Sprint 85.1 — clear GSTIN when leaving B2B so it doesn't silently
                          // leak into the next B2C booking.
                          customer_gstin: t.value === "b2b" ? prev.customer_gstin : "",
                        }))}
                          className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all ${sel ? "border-[#FF6B00] bg-orange-50/50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}
                          data-testid={`user-type-${t.value}`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${sel ? "bg-[#FF6B00]" : "bg-slate-100"}`}>
                            <Icon className={`h-4 w-4 ${sel ? "text-white" : "text-slate-500"}`} />
                          </div>
                          <div className="text-left">
                            <p className={`text-sm font-semibold ${sel ? "text-[#FF6B00]" : "text-slate-700"}`}>{t.label}</p>
                            <p className="text-[10px] text-slate-400">{t.desc}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {form.user_type === "b2b" && (
                    <p className="text-[11px] text-slate-500 pt-1" data-testid="b2b-hint">
                      Business mode: a GSTIN field appears in the contact section above for proper GST invoicing.
                    </p>
                  )}
                </div>

                {/* Delivery Speed selector removed (Feb 2026) — Express is the default for every order. */}

                {/* Pickup Schedule */}
                <div className="space-y-2" data-testid="pickup-schedule-section">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-blue-600" />
                    Pickup Schedule
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, pickup_type: "instant", scheduled_date: "", scheduled_time: "" })}
                      className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${form.pickup_type === "instant" ? "border-[#FF6B00] bg-orange-50/50" : "border-slate-100 bg-slate-50/50"}`}
                      data-testid="pickup-type-instant"
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${form.pickup_type === "instant" ? "bg-[#FF6B00]" : "bg-slate-200"}`}>
                        <Zap className={`h-4 w-4 ${form.pickup_type === "instant" ? "text-white" : "text-slate-400"}`} />
                      </div>
                      <div className="text-left">
                        <p className={`text-sm font-semibold ${form.pickup_type === "instant" ? "text-[#FF6B00]" : "text-slate-700"}`}>Instant</p>
                        <p className="text-[10px] text-slate-400">Pick up now</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, pickup_type: "scheduled" })}
                      className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${form.pickup_type === "scheduled" ? "border-blue-500 bg-blue-50/50" : "border-slate-100 bg-slate-50/50"}`}
                      data-testid="pickup-type-scheduled"
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${form.pickup_type === "scheduled" ? "bg-blue-600" : "bg-slate-200"}`}>
                        <CalendarDays className={`h-4 w-4 ${form.pickup_type === "scheduled" ? "text-white" : "text-slate-400"}`} />
                      </div>
                      <div className="text-left">
                        <p className={`text-sm font-semibold ${form.pickup_type === "scheduled" ? "text-blue-700" : "text-slate-700"}`}>Schedule</p>
                        <p className="text-[10px] text-slate-400">Pick a date & time</p>
                      </div>
                    </button>
                  </div>
                  {form.pickup_type === "scheduled" && (
                    <div className="grid grid-cols-2 gap-3 mt-2 animate-fadeInUp">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Date</Label>
                        <Input
                          type="date"
                          min={new Date().toISOString().split("T")[0]}
                          value={form.scheduled_date}
                          onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                          className="rounded-xl border-slate-200 h-10 focus:border-blue-500 focus:ring-blue-500/20 text-sm"
                          required
                          data-testid="pickup-schedule-date"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Time</Label>
                        <Input
                          type="time"
                          value={form.scheduled_time}
                          onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })}
                          className="rounded-xl border-slate-200 h-10 focus:border-blue-500 focus:ring-blue-500/20 text-sm"
                          required
                          data-testid="pickup-schedule-time"
                        />
                      </div>
                    </div>
                  )}
                </div>


                {/* Delivery Mode: Auto-detected from pickup/drop pincode states; user can override */}
                {form.service_type === "delivery" && (
                <div className="space-y-2" data-testid="delivery-mode-section">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                    <Warehouse className="h-3.5 w-3.5 text-violet-600" />
                    Delivery Mode
                    <span className="text-[10px] text-slate-400 font-normal ml-1" data-testid="delivery-mode-label-tag">
                      {deliveryModeUserOverriddenRef.current
                        ? "(manual)"
                        : _topology === "detecting"
                        ? "(auto-detecting…)"
                        : "(auto-detected)"}
                    </span>
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        deliveryModeUserOverriddenRef.current = true;
                        setForm((prev) => ({ ...prev, delivery_mode: "express" }));
                      }}
                      className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all text-left ${form.delivery_mode === "express" ? "border-[#FF6B00] bg-orange-50/50 shadow-sm" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      data-testid="delivery-mode-express"
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${form.delivery_mode === "express" ? "bg-[#FF6B00]" : "bg-slate-100"}`}>
                        <Zap className={`h-4 w-4 ${form.delivery_mode === "express" ? "text-white" : "text-slate-400"}`} />
                      </div>
                      <div className="text-left">
                        <p className={`text-sm font-semibold ${form.delivery_mode === "express" ? "text-[#FF6B00]" : "text-slate-700"}`}>Express</p>
                        <p className="text-[10px] text-slate-500">Direct — 1 Agent</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        deliveryModeUserOverriddenRef.current = true;
                        setForm((prev) => ({ ...prev, delivery_mode: "hub_routed" }));
                      }}
                      className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all text-left ${form.delivery_mode === "hub_routed" ? "border-violet-400 bg-violet-50/50 ring-1 ring-violet-200 shadow-sm" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      data-testid="delivery-mode-hub-routed"
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${form.delivery_mode === "hub_routed" ? "bg-violet-600" : "bg-slate-100"}`}>
                        <Warehouse className={`h-4 w-4 ${form.delivery_mode === "hub_routed" ? "text-white" : "text-slate-400"}`} />
                      </div>
                      <div className="text-left">
                        <p className={`text-sm font-semibold ${form.delivery_mode === "hub_routed" ? "text-violet-700" : "text-slate-700"}`}>Hub Routed</p>
                        <p className="text-[10px] text-slate-500">Multi-hub — 2 Agents</p>
                      </div>
                    </button>
                  </div>
                  <p className="text-[10px] text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2" data-testid="delivery-mode-subtitle">
                    {form.delivery_mode === "express" && _topology === "same_city"
                      ? `Same-city delivery (${_pickupState || "your city"}) — single agent handles pickup to delivery directly.`
                      : form.delivery_mode === "express"
                      ? "Express — single agent handles pickup to delivery directly."
                      : _topology === "cross_state"
                      ? `Cross-state delivery — ${_pickupState || "origin"} → ${_dropState || "destination"} via origin hub → linehaul → destination hub.`
                      : _topology === "cross_city"
                      ? `Cross-city delivery (within ${_pickupState || "the same state"}) — package routes through hubs with separate pickup & delivery agents.`
                      : "Multi-hub delivery — package routes through logistics hubs with separate pickup & delivery agents."}
                  </p>
                </div>
                )}

                {/* Bundle Selector for micro-shifts */}
                {(form.service_type === "room_shifting" || form.service_type === "luggage_shifting" || form.service_type === "small_furniture") && (
                  <div className="space-y-2" data-testid="bundle-section">
                    <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                      <Package className="h-3.5 w-3.5 text-[#FF6B00]" />
                      Micro-Shift Bundle
                      <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold ml-1">SAVE</span>
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: "studio_move", label: "Studio", items: "10 items", price: "From INR 1,999" },
                        { id: "1rk_move", label: "1RK", items: "20 items", price: "From INR 3,499" },
                        { id: "1bhk_move", label: "1BHK", items: "40 items", price: "From INR 5,999" },
                      ].map((b) => {
                        const sel = form.bundle_id === b.id;
                        return (
                          <button key={b.id} type="button"
                            onClick={() => setForm({ ...form, bundle_id: sel ? "" : b.id, order_type: sel ? "courier" : "micro_shift", item_count: sel ? 1 : parseInt(b.items) })}
                            className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${sel ? "border-[#FF6B00] bg-orange-50/50 ring-1 ring-[#FF6B00]/20" : "border-slate-200 hover:border-slate-300"}`}
                            data-testid={`bundle-${b.id}`}
                          >
                            <Package className={`h-5 w-5 ${sel ? "text-[#FF6B00]" : "text-slate-400"}`} />
                            <span className={`text-xs font-bold ${sel ? "text-[#FF6B00]" : "text-slate-700"}`}>{b.label}</span>
                            <span className="text-[10px] text-slate-400">{b.items}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Item Count for micro-shifts */}
                {form.order_type === "micro_shift" && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Number of Items</Label>
                    <Input type="number" min={1} value={form.item_count} onChange={(e) => setForm({ ...form, item_count: parseInt(e.target.value) || 1 })}
                      className="rounded-xl border-slate-200 h-11 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20" data-testid="order-item-count" />
                    {form.item_count >= 5 && (
                      <p className="text-[10px] text-green-600 font-semibold flex items-center gap-1">
                        <Gift className="h-3 w-3" /> 5+ items: You&apos;ll earn a 20% coupon for your next courier booking!
                      </p>
                    )}
                  </div>
                )}

                {/* Coupon Code */}
                <div className="space-y-2" data-testid="coupon-section">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                    <Tag className="h-3.5 w-3.5 text-[#FF6B00]" />
                    Coupon Code
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter coupon code"
                      value={form.coupon_code}
                      onChange={(e) => { setForm({ ...form, coupon_code: e.target.value.toUpperCase() }); setCouponValid(null); }}
                      className={`rounded-xl border-slate-200 h-11 flex-1 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 font-mono ${couponValid === true ? "border-green-400 ring-1 ring-green-200" : couponValid === false ? "border-red-400 ring-1 ring-red-200" : ""}`}
                      data-testid="coupon-input"
                    />
                    <button type="button" onClick={handleApplyCoupon} disabled={!form.coupon_code.trim()}
                      className="px-4 h-11 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition-colors disabled:opacity-40"
                      data-testid="coupon-apply-btn"
                    >
                      Apply
                    </button>
                  </div>
                  {couponValid === true && <p className="text-[10px] text-green-600 font-semibold">Coupon applied successfully!</p>}
                  {couponValid === false && <p className="text-[10px] text-red-500 font-semibold">Invalid or expired coupon</p>}
                </div>

                {/* Referral first-order discount banner */}
                {referralDiscount?.has_discount && (
                  <div className="flex items-center gap-2.5 rounded-xl bg-green-50 border border-green-200 p-3" data-testid="referral-discount-banner">
                    <Gift className="h-4 w-4 text-green-600 shrink-0" />
                    <p className="text-xs text-green-700 font-semibold leading-relaxed">
                      ₹{referralDiscount.discount} referral discount will be applied automatically on this first order
                      {referralDiscount.referrer_name ? ` — thanks to ${referralDiscount.referrer_name}!` : "!"}
                    </p>
                  </div>
                )}

                {/* Price Estimate — replaced by clean summary when courier is selected */}
                {selectedCourier ? (() => {
                  const _livePct = Number(hubPriceEstimate?.cod_fee_pct ?? priceEstimate?.cod_fee_pct ?? 5);
          const codFee = form.payment_method === "cod" ? Math.round(selectedCourier.courier_price * _livePct / 100) : 0;
                  const refDisc = referralDiscount?.has_discount ? Number(referralDiscount.discount || 0) : 0;
                  const finalTotal = Math.max(selectedCourier.courier_price + codFee - refDisc, 1);
                  return (
                  <div className="rounded-2xl border-2 border-[#FF6B00] bg-gradient-to-br from-orange-50 to-white p-5 shadow-sm" data-testid="selected-courier-summary">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF6B00]">You pay via</span>
                      <button type="button" onClick={() => setSelectedCourier(null)} className="text-[10px] font-bold text-slate-500 hover:text-red-500 uppercase tracking-wider" data-testid="clear-courier-btn">
                        Clear
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-['Outfit'] text-2xl font-bold text-slate-900">{selectedCourier.courier_name}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                          <span>{selectedCourier.estimated_days} day{selectedCourier.estimated_days > 1 ? "s" : ""} delivery</span>
                          <span>⭐ {selectedCourier.reliability}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-['Outfit'] text-3xl font-extrabold text-[#FF6B00]" data-testid="courier-final-price">₹{finalTotal}</p>
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">All inclusive</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-orange-100 space-y-1">
                      <div className="flex items-center justify-between text-xs text-slate-600">
                        <span>Courier charge</span>
                        <span className="font-semibold">₹{selectedCourier.courier_price}</span>
                      </div>
                      {codFee > 0 && (
                        <div className="flex items-center justify-between text-xs text-amber-700" data-testid="cod-fee-row">
                          <span>COD handling fee (5%)</span>
                          <span className="font-semibold">+ ₹{codFee}</span>
                        </div>
                      )}
                      {refDisc > 0 && (
                        <div className="flex items-center justify-between text-xs text-green-700" data-testid="referral-discount-row">
                          <span>Referral discount</span>
                          <span className="font-semibold">− ₹{refDisc}</span>
                        </div>
                      )}
                      <p className="text-[10px] text-slate-400 pt-1">
                        Includes pickup, transport &amp; delivery. {codFee > 0 ? "COD fee covers cash-handling & remittance." : "No packing or hub fees added."}
                      </p>
                    </div>
                  </div>
                  );
                })() : form.delivery_mode === "hub_routed" && hubPriceEstimate && form.service_type === "delivery" ? (
                  <div className="relative">
                    <HubPriceCard
                      pricing={(() => {
                        if (form.payment_method !== "cod") return hubPriceEstimate;
                        const pct = Number(hubPriceEstimate.cod_fee_pct ?? 5);
                        const base = Number(hubPriceEstimate.final_price ?? hubPriceEstimate.total ?? 0);
                        const codFee = Math.round(base * pct / 100);
                        return { ...hubPriceEstimate, cod_fee: codFee, cod_fee_pct: pct, final_price: base + codFee, total: base + codFee };
                      })()}
                      isAdmin={role === "admin"}
                    />
                    {estimateLoading && (
                      <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px] flex items-center justify-center rounded-2xl z-10" data-testid="price-calculating-overlay">
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                          <Loader2 className="h-4 w-4 animate-spin text-[#FF6B00]" />
                          Calculating…
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <PriceCard
                      price={(() => {
                        if (!priceEstimate || form.payment_method !== "cod") return priceEstimate;
                        const pct = Number(priceEstimate.cod_fee_pct ?? 5);
                        const base = Number(priceEstimate.final_price ?? priceEstimate.total ?? priceEstimate.base_total ?? 0);
                        const codFee = Math.round(base * pct / 100);
                        return { ...priceEstimate, cod_fee: codFee, cod_fee_pct: pct, final_price: base + codFee, total: base + codFee, base_total: base + codFee };
                      })()}
                    />
                    {estimateLoading && priceEstimate && (
                      <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px] flex items-center justify-center rounded-2xl z-10" data-testid="price-calculating-overlay">
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                          <Loader2 className="h-4 w-4 animate-spin text-[#FF6B00]" />
                          Calculating…
                        </div>
                      </div>
                    )}
                    {/* Sprint-105c · BUG 2 — when no quote yet (loading or empty
                        inputs), render a price-card skeleton so the booking modal
                        always shows a quote slot, never an empty void. */}
                    {!priceEstimate && (
                      <div
                        className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-6 flex flex-col items-center justify-center gap-2"
                        data-testid="price-card-placeholder"
                      >
                        {estimateLoading ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin text-[#FF6B00]" />
                            <p className="text-xs font-semibold text-slate-600">Calculating live price…</p>
                          </>
                        ) : (
                          <>
                            <IndianRupee className="h-5 w-5 text-slate-400" />
                            <p className="text-xs font-semibold text-slate-600">Enter pickup &amp; drop addresses to see a live price quote</p>
                          </>
                        )}
                      </div>
                    )}
                    {/* Sprint-105c · BUG 2 — multi-parcel incomplete state. The
                        Sprint-105a hotfix marks priceEstimate._incomplete=true
                        when any parcel is missing photo/dims/weight. Surface that
                        clearly instead of leaving a frozen stale quote on screen. */}
                    {priceEstimate && priceEstimate._incomplete && (
                      <div
                        className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2"
                        data-testid="price-card-incomplete-msg"
                      >
                        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-[11px] text-amber-800 font-medium leading-relaxed">
                          Fill all parcel details (name, dimensions, weight, photo) to see the live quote. The amount above is from your last complete state.
                          {multiParcelMissingMsg && <span className="block text-amber-700 font-semibold mt-0.5">{multiParcelMissingMsg}</span>}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Sprint-105c · BUG 3 — Route Preview Map (always-mounted slot)
                    The map renders the route polyline when coords are available.
                    Previously the entire block was gated on priceEstimate; if the
                    multi-parcel quote was incomplete or the API hadn't returned,
                    the map slot vanished. Now we render a skeleton placeholder
                    when coords are pending and the live map when they arrive. */}
                {form.service_type === "delivery" && (() => {
                  const mapPickupLat = priceEstimate?.pickup_lat ?? form.pickup_lat;
                  const mapPickupLng = priceEstimate?.pickup_lng ?? form.pickup_lng;
                  const mapDropLat = priceEstimate?.delivery_lat ?? form.delivery_lat;
                  const mapDropLng = priceEstimate?.delivery_lng ?? form.delivery_lng;
                  const havePickupCoords = Number.isFinite(Number(mapPickupLat)) && Number.isFinite(Number(mapPickupLng));
                  const haveDropCoords = Number.isFinite(Number(mapDropLat)) && Number.isFinite(Number(mapDropLng));
                  if (havePickupCoords && haveDropCoords) {
                    return (
                      <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm" data-testid="route-preview-map">
                        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100">
                          <MapPin className="h-3.5 w-3.5 text-[#FF6B00]" />
                          <span className="text-xs font-semibold text-slate-700">Route Preview</span>
                          {priceEstimate?.distance_km > 0 && (
                            <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded ml-auto">
                              {priceEstimate.distance_source === "osrm" ? "ROAD" : "EST"} {priceEstimate.distance_km} km
                            </span>
                          )}
                        </div>
                        <RoutePreview
                          pickup={{ lat: Number(mapPickupLat), lng: Number(mapPickupLng) }}
                          delivery={{ lat: Number(mapDropLat), lng: Number(mapDropLng) }}
                          distance={priceEstimate?.distance_km}
                        />
                        <div className="flex items-center justify-between px-3 py-2 bg-white">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#FF6B00]" />
                            <span className="text-[10px] text-slate-500 truncate max-w-[150px]">{form.pickup_address}</span>
                          </div>
                          <Navigation className="h-3 w-3 text-slate-400" />
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-slate-500 truncate max-w-[150px]">{form.delivery_address}</span>
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                          </div>
                        </div>
                      </div>
                    );
                  }
                  // Both addresses present but coords still pending → friendly skeleton
                  if ((form.pickup_address || "").length > 2 && (form.delivery_address || "").length > 2) {
                    return (
                      <div
                        className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-3 py-4 flex items-center gap-3"
                        data-testid="route-preview-skeleton"
                      >
                        <MapPin className="h-4 w-4 text-slate-400" />
                        <p className="text-[11px] text-slate-500">
                          {estimateLoading
                            ? "Resolving addresses for map preview…"
                            : "Map preview will appear once both addresses are recognised."}
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Payment Method — visible to all roles (admin can pick customer's payment mode on their behalf) */}
                <div data-testid="payment-method-section">
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Payment Method</Label>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, payment_method: "cod" })}
                        className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all ${
                          form.payment_method === "cod"
                            ? "border-[#FF6B00] bg-orange-50/50 shadow-sm"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid="payment-method-cod"
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${form.payment_method === "cod" ? "bg-[#FF6B00]" : "bg-slate-100"}`}>
                          <IndianRupee className={`h-4 w-4 ${form.payment_method === "cod" ? "text-white" : "text-slate-500"}`} />
                        </div>
                        <div className="text-left">
                          <p className={`text-sm font-semibold ${form.payment_method === "cod" ? "text-[#FF6B00]" : "text-slate-700"}`}>COD</p>
                          <p className="text-[10px] text-slate-400">Pay on delivery</p>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, payment_method: "razorpay" })}
                        className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all ${
                          form.payment_method === "razorpay"
                            ? "border-[#FF6B00] bg-orange-50/50 shadow-sm"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid="payment-method-razorpay"
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${form.payment_method === "razorpay" ? "bg-[#FF6B00]" : "bg-slate-100"}`}>
                          <CreditCard className={`h-4 w-4 ${form.payment_method === "razorpay" ? "text-white" : "text-slate-500"}`} />
                        </div>
                        <div className="text-left">
                          <p className={`text-sm font-semibold ${form.payment_method === "razorpay" ? "text-[#FF6B00]" : "text-slate-700"}`}>Online</p>
                          <p className="text-[10px] text-slate-400">UPI / Card / Net Banking</p>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, payment_method: "wallet", user_type: "b2b" })}
                        disabled={!walletBalance || walletBalance.balance <= 0}
                        className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all ${
                          form.payment_method === "wallet"
                            ? "border-yellow-400 bg-yellow-50/50 shadow-sm ring-1 ring-yellow-200"
                            : !walletBalance || walletBalance.balance <= 0
                            ? "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid="payment-method-wallet"
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${form.payment_method === "wallet" ? "bg-yellow-500" : "bg-slate-100"}`}>
                          <Wallet className={`h-4 w-4 ${form.payment_method === "wallet" ? "text-white" : "text-slate-500"}`} />
                        </div>
                        <div className="text-left">
                          <p className={`text-sm font-semibold ${form.payment_method === "wallet" ? "text-yellow-700" : "text-slate-700"}`}>Wallet</p>
                          <p className="text-[10px] text-slate-400">{walletBalance?.balance > 0 ? `INR ${walletBalance.balance.toLocaleString()}` : "No balance"}</p>
                        </div>
                      </button>
                    </div>
                  </div>

                {/* Courier Comparison (Playbook model) */}
                {form.pickup_address.trim().length >= 3 && form.delivery_address.trim().length >= 3 && role !== "agent" && (
                  <div className="space-y-2" data-testid="courier-compare-section">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Courier</p>
                      <button
                        type="button"
                        onClick={() => setCourierCompareOpen(true)}
                        className="text-xs font-bold text-[#FF6B00] hover:underline flex items-center gap-1"
                        data-testid="compare-couriers-btn"
                      >
                        <Truck className="h-3 w-3" />{selectedCourier ? "Change" : "Compare & Pick"}
                      </button>
                    </div>
                    {selectedCourier ? (
                      <div className="p-3 rounded-xl border-2 border-[#FF6B00] bg-orange-50/50 flex items-center justify-between">
                        <div>
                          <p className="font-['Outfit'] font-bold text-slate-900 text-sm">{selectedCourier.courier_name}</p>
                          <p className="text-[11px] text-slate-500">{selectedCourier.estimated_days} day(s) · ⭐ {selectedCourier.reliability}</p>
                        </div>
                        <p className="font-['Outfit'] text-lg font-bold text-[#FF6B00]">₹{selectedCourier.courier_price}</p>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setCourierCompareOpen(true)}
                        className="w-full p-3 rounded-xl border-2 border-dashed border-orange-300 bg-orange-50/40 text-center cursor-pointer hover:bg-orange-50 transition"
                        data-testid="courier-required-empty"
                      >
                        <p className="text-xs font-bold text-[#FF6B00]">⚠️ Please pick a courier to continue</p>
                        <p className="text-[11px] text-slate-500 mt-1">Tap here or &quot;Compare &amp; Pick&quot; to see options</p>
                      </button>
                    )}
                  </div>
                )}

                {/* §6 Weight cap removed (Feb 2026) — PickNGo accepts any weight on any route. */}

                </div>

                {/* Sprint-105e — sticky footer (caption + Place Order CTA).
                    Anchored to the dialog edge so the user never has to
                    scroll past empty whitespace to find the action. */}
                <div className="flex-shrink-0 px-6 py-3 border-t border-slate-100 bg-white space-y-2" data-testid="create-order-footer">

                {/* Sprint-105d — explicit caption surfacing WHY the submit
                    is blocked. Renders directly above the CTA so the user
                    sees the reason without hovering for a tooltip. */}
                {submitDisabled && submitBlockedReason && (
                  <div
                    className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2"
                    data-testid="submit-blocked-reason"
                  >
                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-[11px] text-amber-800 font-medium leading-relaxed">
                      <span className="font-bold">Almost there —</span> {submitBlockedReason}
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitDisabled}
                  title={submitBlockedReason || undefined}
                  aria-disabled={submitDisabled}
                  className={`w-full rounded-xl h-12 font-semibold shadow-sm btn-press mt-1 transition-colors ${
                    submitDisabled
                      ? "bg-slate-200 text-slate-500 hover:bg-slate-200 cursor-not-allowed shadow-none"
                      : "bg-[#FF6B00] text-white hover:bg-[#E65A00] shadow-[#FF6B00]/20 hover:shadow-md"
                  }`}
                  data-testid="order-submit-btn"
                >
                  {creating
                    ? <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    : form.payment_method === "wallet"
                      ? <Wallet className="h-5 w-5 mr-2" />
                      : form.payment_method === "cod"
                        ? <IndianRupee className="h-5 w-5 mr-2" />
                        : role === "admin"
                          ? <ArrowRight className="h-5 w-5 mr-2" />
                          : <CreditCard className="h-5 w-5 mr-2" />
                  }
                  {creating
                    ? "Processing..."
                    : (form.delivery_mode === "hub_routed" && hubPriceEstimate) || priceEstimate
                      ? (() => {
                          const hubPrice = form.delivery_mode === "hub_routed" && hubPriceEstimate && form.service_type === "delivery"
                            ? hubPriceEstimate.final_price
                            : (priceEstimate?.final_price ?? priceEstimate?.total);
                          let displayPrice = hubPrice;
                          const livePct = Number(hubPriceEstimate?.cod_fee_pct ?? priceEstimate?.cod_fee_pct ?? 5);
                          if (selectedCourier?.courier_price) {
                            const codFee = form.payment_method === "cod" ? Math.round(selectedCourier.courier_price * livePct / 100) : 0;
                            displayPrice = selectedCourier.courier_price + codFee;
                          } else if (form.payment_method === "cod" && Number(displayPrice) > 0) {
                            // Apply admin-configured COD % on regular hub-routed / express totals so the button matches the price card.
                            displayPrice = Number(displayPrice) + Math.round(Number(displayPrice) * livePct / 100);
                          }
                          if (referralDiscount?.has_discount && Number(displayPrice) > Number(referralDiscount.discount || 0)) {
                            displayPrice = Number(displayPrice) - Number(referralDiscount.discount || 0);
                          }
                          const label = getBookingButtonLabel(role, form.payment_method, true);
                          return displayPrice ? `${label} — INR ${displayPrice}` : label;
                        })()
                      : getBookingButtonLabel(role, form.payment_method)}
                </Button>

                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Sprint-92 — group-filter chip from URL param */}
      {groupFilter && FILTER_LABELS[groupFilter] && (
        <div className="flex items-center gap-2 animate-fadeInUp" data-testid="orders-filter-chip">
          <span className="text-xs text-slate-500 font-medium">Showing</span>
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-50 border border-orange-200 text-[#FF6B00] text-sm font-semibold"
            data-testid={`orders-filter-chip-${groupFilter}`}
          >
            {FILTER_LABELS[groupFilter]} orders
            <button
              type="button"
              onClick={clearGroupFilter}
              aria-label="Clear filter"
              className="hover:bg-orange-100 rounded-full p-0.5 transition"
              data-testid="orders-filter-clear"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
          <span className="text-xs text-slate-400">· {filteredOrders.length} result{filteredOrders.length === 1 ? "" : "s"}</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 animate-fadeInUp stagger-1">
        <div className="flex items-center gap-2 bg-white rounded-xl border border-slate-200 px-4 py-2.5 flex-1 max-w-sm focus-within:border-[#FF6B00]/50 focus-within:ring-2 focus-within:ring-[#FF6B00]/10 transition-all">
          <Search className="h-4 w-4 text-slate-400" />
          <input type="text" placeholder="Search orders..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="bg-transparent border-none outline-none text-sm text-slate-600 placeholder:text-slate-400 w-full" data-testid="orders-search-input" />
        </div>
        {/* Phase ASSIGN-FIX-r8 — admin "Pending Assignment" quick chip.
            Toggles the needs_assignment filter group so admin can find the
            "needs a pickup agent" queue without scrolling the full list. */}
        {role === "admin" && (
          <Button
            type="button"
            variant={groupFilter === "needs_assignment" ? "default" : "outline"}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              if (groupFilter === "needs_assignment") next.delete("filter");
              else next.set("filter", "needs_assignment");
              setSearchParams(next, { replace: true });
            }}
            className={`rounded-xl h-11 px-4 ${groupFilter === "needs_assignment"
              ? "bg-[#FF6B00] text-white hover:bg-[#E65A00]"
              : "border-orange-200 text-[#FF6B00] hover:bg-orange-50"}`}
            data-testid="orders-pending-assignment-chip"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Pending Assignment
          </Button>
        )}
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="rounded-xl border-slate-200 w-full sm:w-44 h-11" data-testid="orders-status-filter">
            <Filter className="h-4 w-4 mr-2 text-slate-400" />
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="pickup_assigned">Pickup Assigned</SelectItem>
            <SelectItem value="picked_up">Picked Up</SelectItem>
            <SelectItem value="at_origin_hub">At Origin Hub</SelectItem>
            <SelectItem value="in_linehaul">In Linehaul</SelectItem>
            <SelectItem value="at_dest_hub">At Dest Hub</SelectItem>
            <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
            <SelectItem value="in_transit">In Transit</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <OrderList
        orders={filteredOrders}
        loading={loading}
        role={role}
        updatingId={updatingId}
        onBookFirst={() => setDialogOpen(true)}
        onAcceptPickup={handleAcceptPickup}
        onStatusAdvance={handleStatusAdvance}
        onConfirmCod={handleConfirmCod}
        onVerifyTarget={setVerifyTarget}
        onRate={setRatingTarget}
        onReportDamage={setDamageDialogFor}
        onCancelOrder={setCancelTarget}
        onOpenAssignDialog={({ trackingId, b2bMode, b2bRole, crewMode, crewSize, vehicleType: vt }) => {
          setAssignTarget(trackingId);
          setAssignB2BMode(b2bMode);
          setAssignB2BRole(b2bRole);
          setAssignCrewMode(crewMode);
          setCrewNeeded(crewSize);
          setVehicleType(vt);
          setSelectedAgent("");
          setSelectedCrew([]);
          setAssignDialogOpen(true);
        }}
        onOpenDetails={(tid) => setDetailsTarget(tid)}
        onProofAction={({ order, kind }) => setProofTarget({ order, kind })}
      />

      {/* Full Order Details Dialog — opens on row click */}
      <OrderDetailsDialog
        open={!!detailsTarget}
        onOpenChange={(o) => { if (!o) setDetailsTarget(null); }}
        trackingId={detailsTarget}
        role={role}
      />

      {/* Sprint-83 — QR handoff modal lives at the page level so it's always
          above the booking dialog and doesn't get unmounted by tab switches. */}
      <MobilePhotoQRModal
        open={qrPhotoOpen}
        onClose={() => setQrPhotoOpen(false)}
        onPhotoReceived={handleMobilePhotoReceived}
      />

      {/* Assign Agent Dialog */}
      <AssignAgentDialog
        open={assignDialogOpen}
        onOpenChange={(open) => { setAssignDialogOpen(open); if (!open) { setAssignCrewMode(false); setAssignB2BMode(false); setSelectedCrew([]); setSelectedAgent(""); } }}
        assignTarget={assignTarget}
        assignB2BMode={assignB2BMode}
        assignB2BRole={assignB2BRole}
        assignCrewMode={assignCrewMode}
        crewNeeded={crewNeeded}
        selectedCrew={selectedCrew}
        selectedAgent={selectedAgent}
        setSelectedAgent={setSelectedAgent}
        vehicleType={vehicleType}
        setVehicleType={setVehicleType}
        agentsList={agentsList}
        onAssign={handleAssign}
        onToggleCrewMember={toggleCrewMember}
      />

      {/* OTP Verification Dialog */}
      <OtpVerifyDialog
        open={otpDialogOpen}
        onOpenChange={(open) => { setOtpDialogOpen(open); if (!open) { setOtpTarget(null); setOtpValue(""); } }}
        otpTarget={otpTarget}
        otpValue={otpValue}
        setOtpValue={setOtpValue}
        recipientName={recipientName}
        setRecipientName={setRecipientName}
        deliveryPhoto={deliveryPhoto}
        setDeliveryPhoto={setDeliveryPhoto}
        verifyingOtp={verifyingOtp}
        onVerify={handleVerifyOtp}
      />

      {/* Rating Modal */}
      <RatingModal
        trackingId={ratingTarget}
        open={!!ratingTarget}
        onClose={() => setRatingTarget(null)}
        onRated={fetchOrders}
      />

      {/* Sprint 72 — Proof of pickup / handover / packing / arrival */}
      <ProofActionDialog
        target={proofTarget}
        onClose={() => setProofTarget(null)}
        onDone={fetchOrders}
      />
      {mapPickerMode && (
        <MapPicker
          mode={mapPickerMode}
          onSelect={({ address, lat, lng }) => {
            if (mapPickerMode === "pickup") setForm((f) => ({
              ...f,
              pickup_address: address,
              pickup_lat: lat,
              pickup_lng: lng,
            }));
            else setForm((f) => ({
              ...f,
              delivery_address: address,
              delivery_lat: lat,
              delivery_lng: lng,
            }));
            setMapPickerMode(null);
            // Reopen the booking dialog now that location is picked.
            setDialogOpen(true);
          }}
          onClose={() => {
            setMapPickerMode(null);
            // Reopen the booking dialog so the user keeps their form state.
            setDialogOpen(true);
          }}
        />
      )}
      <BulkUploadDialog open={bulkUploadOpen} onClose={() => setBulkUploadOpen(false)} onSuccess={fetchOrders} />
      <AdminCancelOrderDialog
        order={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={async ({ trackingId, reason }) => {
          // Customer hits /customer-cancel (window-enforced); admin hits /admin-cancel (no window).
          const endpoint = role === "admin"
            ? `/orders/${trackingId}/admin-cancel`
            : `/orders/${trackingId}/customer-cancel`;
          try {
            await api.post(endpoint, { reason });
            toast.success(`Order ${trackingId} cancelled`);
            setCancelTarget(null);
            fetchOrders();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to cancel order");
          }
        }}
      />
      <ReportDamageDialog
        open={!!damageDialogFor}
        onClose={() => setDamageDialogFor(null)}
        order={damageDialogFor}
      />
      <AgentVerifyDialog
        open={!!verifyTarget}
        onOpenChange={(o) => { if (!o) setVerifyTarget(null); }}
        mode={verifyTarget?.mode}
        order={verifyTarget?.order}
        onDone={fetchOrders}
      />
      <CouriersCompareModal
        open={courierCompareOpen}
        onClose={() => setCourierCompareOpen(false)}
        pickup={form.pickup_address}
        delivery={form.delivery_address}
        weight={form.package_weight ? parseFloat(form.package_weight) : 1}
        ownPrice={
          (form.delivery_mode === "hub_routed" && hubPriceEstimate?.final_price)
          || priceEstimate?.final_price
          || priceEstimate?.total
        }
        onSelect={(c) => setSelectedCourier(c)}
      />
    </div>
  );
}
